import { z } from 'zod';

// src/enums.ts
var OrganizationMode = z.enum(["AGENCY", "INTERNAL"]);
var OrganizationStatus = z.enum(["ACTIVE", "SUSPENDED"]);
var UserStatus = z.enum(["ACTIVE", "INACTIVE"]);
var MembershipStatus = z.enum(["ACTIVE", "INACTIVE"]);
var ContextType = z.enum(["CLIENT", "PROJECT", "DEPARTMENT", "INTERNAL"]);
var TaskStatus = z.enum([
  "NOT_STARTED",
  "IN_PROGRESS",
  "WAITING",
  "BLOCKED",
  "PENDING_APPROVAL",
  "COMPLETED",
  "CANCELED"
]);
var TaskPriority = z.enum(["LOW", "NORMAL", "HIGH", "URGENT"]);
var TaskScope = z.enum(["CORPORATE", "PERSONAL"]);
var TaskSource = z.enum(["MANUAL", "AUTOMATION", "CLIENT_FORM", "RECURRENCE", "API"]);
var SystemRole = z.enum(["ADMIN", "MANAGER", "FINANCE", "COLLABORATOR"]);
var ERROR_CATALOG = {
  // Genéricos
  VALIDATION_ERROR: { status: 422, message: "Dados inv\xE1lidos." },
  NOT_FOUND: { status: 404, message: "Recurso n\xE3o encontrado." },
  FORBIDDEN: { status: 403, message: "Voc\xEA n\xE3o tem permiss\xE3o para esta a\xE7\xE3o." },
  UNAUTHORIZED: { status: 401, message: "Autentica\xE7\xE3o necess\xE1ria." },
  CONFLICT: { status: 409, message: "Conflito com o estado atual do recurso." },
  RATE_LIMITED: { status: 429, message: "Muitas requisi\xE7\xF5es. Tente novamente em instantes." },
  INTERNAL_ERROR: { status: 500, message: "Erro interno. Tente novamente." },
  // Auth
  AUTH_INVALID_CREDENTIALS: { status: 401, message: "E-mail ou senha inv\xE1lidos." },
  AUTH_USER_INACTIVE: { status: 403, message: "Usu\xE1rio desativado." },
  AUTH_TOKEN_INVALID: { status: 401, message: "Token inv\xE1lido ou expirado." },
  AUTH_REFRESH_INVALID: { status: 401, message: "Sess\xE3o expirada. Fa\xE7a login novamente." },
  AUTH_ORGANIZATION_REQUIRED: {
    status: 400,
    message: "Selecione uma organiza\xE7\xE3o (header X-Organization-Id)."
  },
  // Tenant
  TENANT_MISMATCH: { status: 403, message: "Recurso n\xE3o pertence \xE0 sua organiza\xE7\xE3o." },
  TENANT_MEMBERSHIP_REQUIRED: { status: 403, message: "Voc\xEA n\xE3o faz parte desta organiza\xE7\xE3o." },
  TENANT_SUSPENDED: { status: 403, message: "Organiza\xE7\xE3o suspensa." },
  // Permissões
  PERMISSION_DENIED: { status: 403, message: "Permiss\xE3o insuficiente." },
  PERSONAL_TASK_FORBIDDEN: { status: 403, message: "Tarefas pessoais s\xE3o privadas." },
  // Tasks (Fase 1+)
  TASK_DEPENDENCY_BLOCKED: { status: 409, message: "A tarefa possui depend\xEAncias pendentes." },
  TASK_DEPENDENCY_CYCLE: { status: 409, message: "Depend\xEAncia criaria um ciclo." },
  TASK_DEPENDENCY_OUTSIDE_PARENT: {
    status: 409,
    message: "Depend\xEAncias s\xF3 podem existir entre subtarefas da mesma tarefa m\xE3e."
  },
  TASK_APPROVAL_REQUIRED: { status: 409, message: "A tarefa exige aprova\xE7\xE3o antes de concluir." },
  TASK_INVALID_TRANSITION: { status: 409, message: "Transi\xE7\xE3o de status inv\xE1lida." },
  TASK_NESTING_LIMIT: { status: 409, message: "S\xF3 existem dois n\xEDveis: tarefa m\xE3e e subtarefa." },
  // Workflow (Fase 2+)
  WORKFLOW_STAGE_LOCKED: { status: 409, message: "Esta etapa n\xE3o permite movimenta\xE7\xE3o manual." },
  WORKFLOW_OVERRIDE_JUSTIFICATION_REQUIRED: {
    status: 422,
    message: "Movimenta\xE7\xE3o for\xE7ada exige justificativa."
  },
  WORKFLOW_TEMPLATE_NOT_PUBLISHED: { status: 409, message: "Template ainda n\xE3o foi publicado." },
  WORKFLOW_VERSION_IMMUTABLE: { status: 409, message: "Vers\xE3o publicada n\xE3o pode ser alterada." },
  WORKFLOW_NO_STAGES: { status: 422, message: "Publique ao menos uma etapa." },
  WORKFLOW_INSTANCE_NOT_ACTIVE: { status: 409, message: "Workflow n\xE3o est\xE1 ativo." },
  WORKFLOW_STAGE_INVALID: { status: 422, message: "Etapa de destino inv\xE1lida." },
  WORKFLOW_ALREADY_ATTACHED: { status: 409, message: "Tarefa j\xE1 pertence a um workflow." },
  WORKFLOW_LOCKED: { status: 409, message: "Workflow em processamento. Tente novamente." },
  RECURRENCE_INVALID_RULE: { status: 422, message: "Regra de recorr\xEAncia inv\xE1lida." },
  AUTOMATION_INVALID: { status: 422, message: "Automa\xE7\xE3o inv\xE1lida." },
  // Usuários
  USER_EMAIL_TAKEN: { status: 409, message: "E-mail j\xE1 cadastrado." },
  USER_SHARED_ACCOUNT: {
    status: 409,
    message: "Este usu\xE1rio tamb\xE9m participa de outra organiza\xE7\xE3o: e-mail e senha s\xF3 podem ser alterados por ele mesmo."
  },
  PASSWORD_CURRENT_INVALID: { status: 422, message: "A senha atual n\xE3o confere." },
  AVATAR_INVALID: { status: 422, message: "Foto inv\xE1lida: use PNG, JPG ou WEBP com at\xE9 256 KB." },
  USER_DEACTIVATION_NEEDS_SUCCESSOR: {
    status: 422,
    message: "Informe o usu\xE1rio que receber\xE1 as tarefas pendentes."
  },
  // Tasks (Fase 1)
  TASK_PERSONAL_NO_CONTEXT: {
    status: 422,
    message: "Tarefa pessoal n\xE3o aceita participantes nem contexto."
  },
  TASK_RESPONSIBLE_NOT_MEMBER: {
    status: 422,
    message: "Respons\xE1vel precisa ser membro ativo da organiza\xE7\xE3o."
  },
  TASK_SCOPE_MISMATCH: { status: 409, message: "Subtarefa deve ter o mesmo escopo da tarefa m\xE3e." },
  TASK_IS_DELETED: { status: 409, message: "Tarefa est\xE1 na lixeira. Restaure antes de editar." },
  TASK_PARENT_CANCELED: {
    status: 409,
    message: "Tarefa m\xE3e cancelada n\xE3o aceita novas subtarefas."
  },
  DEPENDENCY_SELF: { status: 409, message: "Uma tarefa n\xE3o pode depender de si mesma." },
  DEPENDENCY_ONLY_SUBTASKS: {
    status: 409,
    message: "Na V1, depend\xEAncias s\xF3 existem entre subtarefas."
  },
  // Teams / clients / tags
  TEAM_NAME_TAKEN: { status: 409, message: "J\xE1 existe um time com esse nome." },
  FUNCTION_NAME_TAKEN: { status: 409, message: "J\xE1 existe uma fun\xE7\xE3o com esse nome." },
  TAG_NAME_TAKEN: { status: 409, message: "J\xE1 existe uma tag com esse nome." },
  TAG_SYSTEM_READONLY: { status: 409, message: "Tags de sistema n\xE3o podem ser editadas." },
  USER_NOT_MEMBER: { status: 422, message: "Usu\xE1rio n\xE3o faz parte da organiza\xE7\xE3o." },
  USER_SELF_DEACTIVATION: { status: 409, message: "Voc\xEA n\xE3o pode desativar a si mesmo." },
  FEATURE_DISABLED: { status: 403, message: "M\xF3dulo indispon\xEDvel no modo desta organiza\xE7\xE3o." },
  // Attachments
  ATTACHMENT_NOT_UPLOADED: { status: 409, message: "Arquivo ainda n\xE3o foi enviado ao storage." },
  ATTACHMENT_SIZE_MISMATCH: {
    status: 422,
    message: "Tamanho do arquivo n\xE3o confere com o informado."
  },
  STORAGE_UNAVAILABLE: { status: 503, message: "Storage de arquivos indispon\xEDvel." },
  // Formulários externos (Fase 3)
  FORM_UNAVAILABLE: { status: 404, message: "Formul\xE1rio indispon\xEDvel." },
  FORM_TOKEN_REQUIRED: { status: 401, message: "Este formul\xE1rio exige um token de acesso." },
  FORM_TOKEN_INVALID: { status: 403, message: "Token de acesso inv\xE1lido." },
  FORM_AUTH_REQUIRED: { status: 401, message: "Este formul\xE1rio exige autentica\xE7\xE3o." },
  FORM_SUBMISSION_INVALID: {
    status: 422,
    message: "Preencha os campos obrigat\xF3rios corretamente."
  },
  EXTERNAL_REQUEST_ALREADY_DECIDED: {
    status: 409,
    message: "Solicita\xE7\xE3o j\xE1 foi aprovada ou rejeitada."
  },
  // Gestão (Fase 4)
  TIMER_ALREADY_RUNNING: { status: 409, message: "Voc\xEA j\xE1 tem um cron\xF4metro rodando." },
  TIMER_NOT_RUNNING: { status: 409, message: "Nenhum cron\xF4metro rodando nesta tarefa." },
  CUSTOM_FIELD_INVALID: { status: 422, message: "Valor inv\xE1lido para campo personalizado." },
  REPORT_EXPORT_DENIED: { status: 403, message: "Exportar relat\xF3rios exige permiss\xE3o espec\xEDfica." },
  // Pipelines
  PIPELINE_STAGE_INVALID: { status: 422, message: "Coluna n\xE3o pertence a este pipeline." },
  PIPELINE_NOT_MANUAL: {
    status: 422,
    message: "Pipeline de workflow: as demandas entram pelo workflow, n\xE3o pela coluna."
  },
  PIPELINE_PERSONAL_TASK: {
    status: 422,
    message: "Tarefa pessoal n\xE3o entra em pipeline."
  },
  PIPELINE_TASK_OUTSIDE: { status: 409, message: "A demanda n\xE3o est\xE1 neste pipeline." },
  PIPELINE_WORKFLOW_IMMUTABLE: {
    status: 422,
    message: "As colunas de um pipeline de workflow v\xEAm das etapas do workflow."
  },
  // Parâmetros e dados da empresa
  LOGO_INVALID: {
    status: 422,
    message: "Logotipo inv\xE1lido: use PNG, JPG, WEBP ou SVG com at\xE9 512 KB."
  },
  MAIL_NOT_CONFIGURED: { status: 409, message: "O envio de e-mail n\xE3o est\xE1 configurado." },
  MAIL_TEST_FAILED: { status: 502, message: "O servidor SMTP recusou o envio de teste." },
  CHANNEL_SETTINGS_INCOMPLETE: {
    status: 422,
    message: "Preencha os dados obrigat\xF3rios do canal antes de ativ\xE1-lo."
  },
  CHANNEL_TEST_FAILED: { status: 502, message: "O servi\xE7o recusou o teste do canal." },
  // Integrações (Fase 5)
  CHANNEL_UNAVAILABLE: {
    status: 503,
    message: "Este canal n\xE3o est\xE1 configurado nesta instala\xE7\xE3o."
  },
  TELEGRAM_NOT_LINKED: { status: 409, message: "Vincule sua conta do Telegram primeiro." },
  CALENDAR_PROVIDER_UNAVAILABLE: {
    status: 503,
    message: "Provider de calend\xE1rio n\xE3o configurado nesta instala\xE7\xE3o."
  },
  CALENDAR_CONNECTION_FAILED: { status: 422, message: "N\xE3o foi poss\xEDvel conectar ao calend\xE1rio." },
  OAUTH_STATE_INVALID: { status: 400, message: "Autoriza\xE7\xE3o inv\xE1lida ou expirada." },
  // Comercial e cofre (Fase 6)
  PROPOSAL_NOT_EDITABLE: { status: 409, message: "S\xF3 propostas em rascunho podem ser editadas." },
  PROPOSAL_INVALID_TRANSITION: {
    status: 409,
    message: "Transi\xE7\xE3o de status inv\xE1lida para a proposta."
  },
  PROPOSAL_EMPTY: { status: 422, message: "A proposta precisa de ao menos um item." },
  CONTRACT_INVALID_TRANSITION: {
    status: 409,
    message: "Transi\xE7\xE3o de status inv\xE1lida para o contrato."
  },
  CONTRACT_NOT_EDITABLE: { status: 409, message: "S\xF3 contratos em rascunho podem ser editados." },
  VAULT_KEY_MISSING: {
    status: 503,
    message: "Cofre indispon\xEDvel: configure VAULT_MASTER_KEY no servidor."
  },
  // Portal do cliente (Fase 7)
  PORTAL_INVITE_INVALID: { status: 400, message: "Convite inv\xE1lido ou expirado." },
  PORTAL_NOT_ENABLED: { status: 403, message: "Este contato n\xE3o tem acesso ao portal." },
  PORTAL_CONTACT_EMAIL_REQUIRED: {
    status: 422,
    message: "O contato precisa ter e-mail para acessar o portal."
  },
  PORTAL_TASK_NOT_VISIBLE: { status: 404, message: "Demanda n\xE3o encontrada." },
  PORTAL_APPROVAL_NOT_PENDING: { status: 409, message: "Esta demanda n\xE3o aguarda sua aprova\xE7\xE3o." },
  TASK_NOT_VISIBLE_TO_CLIENT: {
    status: 409,
    message: "Marque a demanda como vis\xEDvel ao cliente antes de pedir aprova\xE7\xE3o dele."
  },
  // Exclusão
  HARD_DELETE_CONFIRMATION_MISMATCH: {
    status: 422,
    message: "Texto de confirma\xE7\xE3o n\xE3o confere."
  }
};
var ErrorCode = z.enum(Object.keys(ERROR_CATALOG));
var ApiErrorSchema = z.object({
  error: z.object({
    code: ErrorCode,
    message: z.string(),
    details: z.record(z.string(), z.unknown()).optional()
  })
});
var PaginationMetaSchema = z.object({
  page: z.number().int().min(1),
  per_page: z.number().int().min(1),
  total: z.number().int().min(0)
});
var PaginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  per_page: z.coerce.number().int().min(1).max(200).default(50)
});
var IdSchema = z.uuid();
var SlugSchema = z.string().min(2).max(64).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Use letras min\xFAsculas, n\xFAmeros e h\xEDfens.");
var FontSansKey = z.enum([
  "bricolage-grotesque",
  "quicksand",
  "ibm-plex-sans",
  "be-vietnam-pro",
  "google-sans",
  "smooch-sans",
  "share-tech",
  "barlow-condensed"
]);
var FontMonoKey = z.enum([
  "jetbrains-mono",
  "ibm-plex-mono",
  "space-mono",
  "fira-code",
  "courier-prime",
  "google-sans-code",
  "share-tech-mono",
  "fira-mono",
  "lekton",
  "menlo"
]);
var DEFAULT_FONT_SANS = "ibm-plex-sans";
var DEFAULT_FONT_MONO = "jetbrains-mono";

// src/appearance.ts
var ThemeMode = z.enum(["dark", "light", "system"]);
var AccentKey = z.enum(["cyan", "violet", "lime", "amber", "rose", "blue"]);
var Density = z.enum(["compact", "comfortable"]);
var AppearanceSchema = z.object({
  theme: ThemeMode.default("dark"),
  accent: AccentKey.default("cyan"),
  density: Density.default("compact"),
  font_sans: FontSansKey.default(DEFAULT_FONT_SANS),
  font_mono: FontMonoKey.default(DEFAULT_FONT_MONO)
});
var AppearancePatchSchema = z.object({
  theme: ThemeMode.optional(),
  accent: AccentKey.optional(),
  density: Density.optional(),
  font_sans: FontSansKey.optional(),
  font_mono: FontMonoKey.optional()
});
var DEFAULT_APPEARANCE = AppearanceSchema.parse({});

// src/auth.ts
var LoginSchema = z.object({
  email: z.email().max(254),
  password: z.string().min(8).max(128),
  /** Opcional: quando o usuário pertence a várias organizações. */
  organization_id: z.uuid().optional()
});
var AuthOrganizationSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  slug: z.string(),
  mode: OrganizationMode
});
var AuthUserSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  email: z.string()
});
var AuthSessionSchema = z.object({
  access_token: z.string(),
  expires_in: z.number().int(),
  user: AuthUserSchema,
  organization: AuthOrganizationSchema.nullable(),
  organizations: z.array(AuthOrganizationSchema)
});
var MeSchema = z.object({
  user: AuthUserSchema.extend({
    phone: z.string().nullable(),
    avatar_url: z.string().nullable()
  }),
  organization: AuthOrganizationSchema.nullable(),
  organizations: z.array(AuthOrganizationSchema),
  roles: z.array(z.object({ id: z.uuid(), name: z.string(), system_role: SystemRole.nullable() })),
  permissions: z.array(z.string()),
  appearance: AppearanceSchema
});

// src/permissions.ts
var PERMISSIONS = {
  // Tasks
  "tasks.view_own": "Ver as pr\xF3prias tarefas",
  "tasks.view_team": "Ver tarefas dos times sob gest\xE3o",
  "tasks.view_all": "Ver todas as tarefas da organiza\xE7\xE3o",
  "tasks.create": "Criar tarefas",
  "tasks.edit": "Editar tarefas",
  "tasks.delete": "Excluir tarefas (lixeira)",
  "tasks.force_delete": "Excluir tarefas definitivamente",
  "tasks.assign": "Atribuir respons\xE1vel",
  "tasks.change_stage": "Mover etapa de workflow",
  "tasks.complete": "Concluir tarefas de terceiros",
  "tasks.approve": "Aprovar ou reprovar tarefas",
  "tasks.override_workflow": "For\xE7ar movimenta\xE7\xE3o de etapa",
  "tasks.manage_tags": "Gerenciar tags",
  // Clients
  "clients.view": "Ver clientes",
  "clients.create": "Criar clientes",
  "clients.edit": "Editar clientes",
  "clients.delete": "Excluir clientes (lixeira)",
  "clients.force_delete": "Excluir clientes definitivamente",
  "clients.view_credentials": "Ver credenciais do cofre",
  "clients.manage_credentials": "Gerenciar credenciais do cofre",
  // Users
  "users.view": "Ver usu\xE1rios",
  "users.create": "Criar usu\xE1rios",
  "users.edit": "Editar usu\xE1rios",
  "users.deactivate": "Desativar usu\xE1rios",
  "users.manage_roles": "Gerenciar perfis e permiss\xF5es",
  // Teams
  "teams.view": "Ver times",
  "teams.create": "Criar times",
  "teams.edit": "Editar times",
  "teams.delete": "Excluir times",
  // Commercial
  "services.view": "Ver servi\xE7os",
  "services.manage": "Gerenciar servi\xE7os",
  "proposals.view": "Ver propostas",
  "proposals.create": "Criar propostas",
  "proposals.approve": "Aprovar propostas",
  "contracts.view": "Ver contratos",
  "contracts.manage": "Gerenciar contratos",
  "financial.view": "Ver dados financeiros",
  "financial.edit": "Editar dados financeiros",
  // Workflows
  "workflows.view": "Ver workflows",
  "workflows.manage": "Gerenciar workflows",
  "workflows.publish": "Publicar vers\xF5es de workflow",
  // Pipelines
  "pipelines.manage": "Criar e editar pipelines (colunas e membros)",
  // Forms
  "forms.view": "Ver formul\xE1rios",
  "forms.manage": "Gerenciar formul\xE1rios",
  "external_requests.triage": "Triar solicita\xE7\xF5es externas",
  "external_requests.approve": "Aprovar solicita\xE7\xF5es externas",
  "external_requests.reject": "Rejeitar solicita\xE7\xF5es externas",
  // Reports
  "reports.view": "Ver relat\xF3rios",
  "reports.export": "Exportar relat\xF3rios",
  // Settings
  "settings.manage": "Gerenciar configura\xE7\xF5es da organiza\xE7\xE3o",
  "integrations.manage": "Gerenciar integra\xE7\xF5es"
};
var PERMISSION_CODES = Object.keys(PERMISSIONS);
var SYSTEM_ROLE_PRESETS = {
  ADMIN: PERMISSION_CODES,
  MANAGER: [
    "tasks.view_own",
    "tasks.view_team",
    "tasks.create",
    "tasks.edit",
    "tasks.delete",
    "tasks.assign",
    "tasks.change_stage",
    "tasks.complete",
    "tasks.approve",
    "tasks.manage_tags",
    "clients.view",
    "clients.create",
    "clients.edit",
    "users.view",
    "teams.view",
    "teams.edit",
    "services.view",
    "proposals.view",
    "contracts.view",
    "workflows.view",
    "pipelines.manage",
    "forms.view",
    "external_requests.triage",
    "external_requests.approve",
    "external_requests.reject",
    "reports.view",
    "reports.export"
  ],
  FINANCE: [
    "tasks.view_own",
    "clients.view",
    "users.view",
    "services.view",
    "services.manage",
    "proposals.view",
    "proposals.create",
    "proposals.approve",
    "contracts.view",
    "contracts.manage",
    "financial.view",
    "financial.edit",
    "reports.view",
    "reports.export"
  ],
  COLLABORATOR: ["tasks.view_own", "tasks.create", "clients.view", "users.view", "teams.view"]
};
var UserSummarySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  email: z.string(),
  status: UserStatus.optional()
});
var RoleSummarySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  system_role: SystemRole.nullable()
});
var RoleSchema = RoleSummarySchema.extend({
  permissions: z.array(z.string())
});
var OrgUserSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  email: z.string(),
  phone: z.string().nullable(),
  status: UserStatus,
  membership_status: MembershipStatus,
  roles: z.array(RoleSummarySchema),
  /** Foto de perfil (rota pública com chave aleatória) ou null. */
  avatar_url: z.string().nullable(),
  /** Participa de outras organizações: e-mail e senha só podem ser trocados por ele mesmo. */
  shared_account: z.boolean(),
  last_login_at: z.iso.datetime().nullable(),
  created_at: z.iso.datetime()
});
var CreateUserSchema = z.object({
  name: z.string().trim().min(2).max(120),
  email: z.email().max(254),
  password: z.string().min(8).max(128),
  phone: z.string().trim().max(32).optional(),
  role_ids: z.array(z.uuid()).default([])
});
var UpdateUserSchema = z.object({
  name: z.string().trim().min(2).max(120).optional(),
  email: z.email().max(254).optional(),
  phone: z.string().trim().max(32).nullable().optional(),
  /** Nova senha definida pelo admin; encerra as sessões abertas do usuário. */
  password: z.string().min(8, "A senha precisa ter ao menos 8 caracteres.").max(128).optional()
});
var MyProfileSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  email: z.string(),
  phone: z.string().nullable(),
  avatar_url: z.string().nullable()
});
var UpdateMyProfileSchema = z.object({
  phone: z.string().trim().max(32).nullable()
});
var ChangePasswordSchema = z.object({
  current_password: z.string().min(1).max(128),
  new_password: z.string().min(8, "A senha precisa ter ao menos 8 caracteres.").max(128)
});
var AVATAR_MIME_TYPES = ["image/png", "image/jpeg", "image/webp"];
var AVATAR_MAX_BYTES = 256 * 1024;
var AvatarUploadSchema = z.object({
  mime_type: z.enum(AVATAR_MIME_TYPES),
  data_base64: z.string().min(1).max(Math.ceil(AVATAR_MAX_BYTES * 4 / 3) + 8)
});
var SetUserRolesSchema = z.object({ role_ids: z.array(z.uuid()) });
var DeactivateUserSchema = z.object({
  successor_user_id: z.uuid().optional()
});
var DeactivationPreviewSchema = z.object({
  pending_tasks: z.number().int(),
  teams: z.number().int(),
  pending_approvals: z.number().int()
});
var UserListQuerySchema = z.object({
  q: z.string().trim().max(120).optional(),
  status: UserStatus.optional(),
  page: z.coerce.number().int().min(1).default(1),
  per_page: z.coerce.number().int().min(1).max(200).default(50)
});
var TeamMemberRole = z.enum(["MEMBER", "MANAGER"]);
var FunctionSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().nullable()
});
var CreateFunctionSchema = z.object({
  name: z.string().trim().min(2).max(80),
  description: z.string().trim().max(500).optional()
});
var UpdateFunctionSchema = CreateFunctionSchema.partial();
var TeamMemberInputSchema = z.object({
  user_id: z.uuid(),
  role: TeamMemberRole.default("MEMBER"),
  function_ids: z.array(z.uuid()).default([])
});
var TeamMemberSchema = z.object({
  user: UserSummarySchema,
  role: TeamMemberRole,
  functions: z.array(FunctionSchema.pick({ id: true, name: true }))
});
var TeamSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  status: z.enum(["ACTIVE", "INACTIVE"]),
  members: z.array(TeamMemberSchema),
  created_at: z.iso.datetime()
});
var CreateTeamSchema = z.object({
  name: z.string().trim().min(2).max(80),
  description: z.string().trim().max(500).optional(),
  members: z.array(TeamMemberInputSchema).default([])
});
var UpdateTeamSchema = z.object({
  name: z.string().trim().min(2).max(80).optional(),
  description: z.string().trim().max(500).nullable().optional(),
  status: z.enum(["ACTIVE", "INACTIVE"]).optional()
});
var SetTeamMembersSchema = z.object({ members: z.array(TeamMemberInputSchema) });
var ContextStatus = z.enum(["ACTIVE", "ARCHIVED"]);
var PersonType = z.enum(["INDIVIDUAL", "COMPANY"]);
var ContextSchema = z.object({
  id: z.uuid(),
  type: ContextType,
  name: z.string(),
  description: z.string().nullable(),
  status: ContextStatus,
  client_id: z.uuid().nullable(),
  created_at: z.iso.datetime()
});
var CreateContextSchema = z.object({
  type: z.enum(["PROJECT", "DEPARTMENT", "INTERNAL"]),
  name: z.string().trim().min(2).max(120),
  description: z.string().trim().max(1e3).optional()
});
var UpdateContextSchema = z.object({
  name: z.string().trim().min(2).max(120).optional(),
  description: z.string().trim().max(1e3).nullable().optional(),
  status: ContextStatus.optional()
});
var ContextListQuerySchema = z.object({
  type: ContextType.optional(),
  status: ContextStatus.optional(),
  q: z.string().trim().max(120).optional()
});
var AddressSchema = z.object({
  street: z.string().max(200).optional(),
  number: z.string().max(20).optional(),
  complement: z.string().max(100).optional(),
  district: z.string().max(100).optional(),
  city: z.string().max(100).optional(),
  state: z.string().max(2).optional(),
  zip: z.string().max(12).optional(),
  country: z.string().max(2).default("BR")
});
var cleanDocument = (v) => v ? v.replace(/[^0-9A-Za-z]/g, "").toUpperCase() : v;
var ClientBaseSchema = z.object({
  person_type: PersonType,
  legal_name: z.string().trim().min(2).max(200),
  trade_name: z.string().trim().max(200).optional(),
  document: z.string().trim().max(20).transform(cleanDocument).optional(),
  state_registration: z.string().trim().max(30).optional(),
  municipal_registration: z.string().trim().max(30).optional(),
  email: z.email().max(254).optional().or(z.literal("")),
  phone: z.string().trim().max(32).optional(),
  website: z.string().trim().max(200).optional(),
  address: AddressSchema.optional(),
  extra: z.record(z.string(), z.unknown()).optional()
});
var CreateClientSchema = ClientBaseSchema.superRefine((v, ctx) => {
  if (!v.document) return;
  const ok = v.person_type === "INDIVIDUAL" ? /^\d{11}$/.test(v.document) : /^[0-9A-Z]{12}\d{2}$/.test(v.document);
  if (!ok) {
    ctx.addIssue({
      code: "custom",
      path: ["document"],
      message: v.person_type === "INDIVIDUAL" ? "CPF deve ter 11 d\xEDgitos." : "CNPJ deve ter 14 caracteres (12 letras ou n\xFAmeros e 2 d\xEDgitos finais)."
    });
  }
});
var UpdateClientSchema = ClientBaseSchema.partial().extend({
  status: z.enum(["ACTIVE", "INACTIVE"]).optional()
});
var ClientSchema = z.object({
  id: z.uuid(),
  context_id: z.uuid(),
  person_type: PersonType,
  legal_name: z.string(),
  trade_name: z.string().nullable(),
  document: z.string().nullable(),
  state_registration: z.string().nullable(),
  municipal_registration: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  website: z.string().nullable(),
  address: AddressSchema.nullable(),
  extra: z.record(z.string(), z.unknown()).nullable(),
  status: z.enum(["ACTIVE", "INACTIVE"]),
  contacts_count: z.number().int().optional(),
  open_tasks: z.number().int().optional(),
  created_at: z.iso.datetime(),
  deleted_at: z.iso.datetime().nullable()
});
var ClientListQuerySchema = z.object({
  q: z.string().trim().max(120).optional(),
  status: z.enum(["ACTIVE", "INACTIVE"]).optional(),
  person_type: PersonType.optional(),
  trashed: z.coerce.boolean().default(false),
  page: z.coerce.number().int().min(1).default(1),
  per_page: z.coerce.number().int().min(1).max(200).default(50)
});
var ClientContactSchema = z.object({
  id: z.uuid(),
  client_id: z.uuid(),
  name: z.string(),
  role: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  whatsapp: z.string().nullable(),
  notes: z.string().nullable(),
  status: z.enum(["ACTIVE", "INACTIVE"]),
  portal: z.object({
    enabled: z.boolean(),
    invited_at: z.iso.datetime().nullable(),
    invite_expires_at: z.iso.datetime().nullable(),
    last_login_at: z.iso.datetime().nullable()
  })
});
var CreateClientContactSchema = z.object({
  name: z.string().trim().min(2).max(120),
  role: z.string().trim().max(80).optional(),
  email: z.email().max(254).optional().or(z.literal("")),
  phone: z.string().trim().max(32).optional(),
  whatsapp: z.string().trim().max(32).optional(),
  notes: z.string().trim().max(1e3).optional()
});
var UpdateClientContactSchema = CreateClientContactSchema.partial().extend({
  status: z.enum(["ACTIVE", "INACTIVE"]).optional()
});
var TagType = z.enum(["USER", "SYSTEM"]);
var TagSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  type: TagType,
  color: z.string().nullable()
});
var HexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, "Use cor hexadecimal (#RRGGBB).");
var CreateTagSchema = z.object({
  name: z.string().trim().min(1).max(40),
  color: HexColor.optional()
});
var UpdateTagSchema = z.object({
  name: z.string().trim().min(1).max(40).optional(),
  color: HexColor.nullable().optional()
});
var PIPELINE_GENERAL = "general";
var PipelineSource = z.enum(["MANUAL", "WORKFLOW"]);
var PipelineStageCategory = z.enum([
  "NOT_STARTED",
  "IN_PROGRESS",
  "WAITING",
  "BLOCKED",
  "COMPLETED"
]);
var PIPELINE_CATEGORY_LABEL = {
  NOT_STARTED: "N\xE3o iniciada",
  IN_PROGRESS: "Em andamento",
  WAITING: "Aguardando",
  BLOCKED: "Bloqueada",
  COMPLETED: "Conclu\xEDda"
};
function categoryOfStatus(status) {
  if (status === "PENDING_APPROVAL") return "COMPLETED";
  if (status === "CANCELED") return null;
  return status;
}
var PIPELINE_COLORS = [
  "var(--text-3)",
  "var(--info)",
  "var(--accent)",
  "var(--violet)",
  "var(--warning)",
  "var(--danger)",
  "var(--success)"
];
var PipelineColor = z.enum(PIPELINE_COLORS);
var PipelineStageSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  color: z.string(),
  category: PipelineStageCategory,
  sort_order: z.number().int(),
  /** Demandas (não excluídas) nesta coluna; só na leitura do pipeline. */
  task_count: z.number().int().optional()
});
var PipelineSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  source: PipelineSource,
  workflow_template: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  stages: z.array(PipelineStageSchema),
  members: z.array(UserSummarySchema),
  /** Quem vê pode mover cartões; quem tem pipelines.manage também edita. */
  can_manage: z.boolean(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime()
});
var TaskPipelineRefSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  stage: z.object({
    id: z.uuid(),
    name: z.string(),
    color: z.string(),
    category: PipelineStageCategory
  }).nullable()
});
var StageInput = z.object({
  /** Ausente = coluna nova. */
  id: z.uuid().optional(),
  name: z.string().trim().min(1, "D\xEA um nome \xE0 coluna.").max(60),
  color: PipelineColor.default("var(--text-3)"),
  category: PipelineStageCategory
});
var Stages = z.array(StageInput).min(1, "O pipeline precisa de pelo menos uma coluna.").max(20, "Use at\xE9 20 colunas.");
var CreatePipelineSchema = z.object({
  name: z.string().trim().min(2, "D\xEA um nome ao pipeline.").max(80),
  description: z.string().trim().max(500).nullable().optional(),
  source: PipelineSource.default("MANUAL"),
  workflow_template_id: z.uuid().nullable().optional(),
  stages: Stages.optional(),
  member_ids: z.array(z.uuid()).max(500).default([])
}).refine((v) => v.source !== "WORKFLOW" || v.workflow_template_id, {
  message: "Escolha o workflow.",
  path: ["workflow_template_id"]
}).refine((v) => v.source !== "MANUAL" || v.stages && v.stages.length > 0, {
  message: "O pipeline precisa de pelo menos uma coluna.",
  path: ["stages"]
});
var UpdatePipelineSchema = z.object({
  name: z.string().trim().min(2).max(80).optional(),
  description: z.string().trim().max(500).nullable().optional(),
  stages: Stages.optional(),
  member_ids: z.array(z.uuid()).max(500).optional()
}).refine((v) => Object.values(v).some((x) => x !== void 0), { message: "Nada para alterar." });
var MovePipelineStageSchema = z.object({
  stage_id: z.uuid(),
  comment: z.string().trim().max(2e3).optional()
});
var DEFAULT_PIPELINE_STAGES = [
  { name: "A fazer", color: "var(--text-3)", category: "NOT_STARTED" },
  { name: "Em andamento", color: "var(--info)", category: "IN_PROGRESS" },
  { name: "Conclu\xEDdo", color: "var(--success)", category: "COMPLETED" }
];

// src/tasks.ts
var Money = z.union([z.number(), z.string()]).transform((v) => typeof v === "number" ? v.toFixed(2) : v).pipe(z.string().regex(/^\d+(\.\d{1,2})?$/, "Valor monet\xE1rio inv\xE1lido."));
var TaskContextRefSchema = z.object({ id: z.uuid(), name: z.string(), type: ContextType });
var TaskRefSchema = z.object({
  id: z.uuid(),
  number: z.number().int(),
  title: z.string(),
  status: TaskStatus
});
var TaskSummarySchema = z.object({
  id: z.uuid(),
  number: z.number().int(),
  title: z.string(),
  scope: TaskScope,
  source: TaskSource,
  status: TaskStatus,
  priority: TaskPriority,
  parent_id: z.uuid().nullable(),
  context: TaskContextRefSchema.nullable(),
  responsible: UserSummarySchema.nullable(),
  participants: z.array(UserSummarySchema),
  tags: z.array(TagSchema),
  due_at: z.iso.datetime().nullable(),
  started_at: z.iso.datetime().nullable(),
  completed_at: z.iso.datetime().nullable(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
  /** Derivado (RULE-012): due_at < agora e status aberto. */
  is_overdue: z.boolean(),
  /** Subtarefa aguardando dependência obrigatória (RULE-010). */
  is_blocked_by_dependency: z.boolean(),
  subtasks_total: z.number().int(),
  subtasks_completed: z.number().int(),
  requires_approval: z.boolean(),
  approval_by_client: z.boolean(),
  visible_to_client: z.boolean(),
  is_required: z.boolean(),
  workflow_stage_instance_id: z.uuid().nullable(),
  workflow_instance_id: z.uuid().nullable(),
  /** Presente quando a tarefa (mãe) pertence a um workflow. */
  workflow: z.object({
    instance_id: z.uuid(),
    template_id: z.uuid(),
    template_name: z.string(),
    stage: z.object({
      id: z.uuid(),
      name: z.string(),
      color: z.string().nullable(),
      sort_order: z.number().int()
    }).nullable()
  }).nullable(),
  /** Pipeline manual da demanda (coluna efetiva já resolvida pelo status); null = Geral. */
  pipeline: TaskPipelineRefSchema.nullable(),
  estimated_minutes: z.number().int().nullable(),
  tracked_minutes: z.number().int().nullable()
});
var TaskDependencySchema = z.object({
  id: z.uuid(),
  task: TaskRefSchema,
  depends_on: TaskRefSchema,
  is_required: z.boolean()
});
var ChecklistItemSchema = z.object({
  id: z.uuid(),
  text: z.string(),
  is_completed: z.boolean(),
  completed_by: z.uuid().nullable(),
  completed_at: z.iso.datetime().nullable(),
  sort_order: z.number().int()
});
var ChecklistSchema = z.object({
  id: z.uuid(),
  task_id: z.uuid(),
  title: z.string(),
  sort_order: z.number().int(),
  items: z.array(ChecklistItemSchema)
});
var TaskAbilitiesSchema = z.object({
  edit: z.boolean(),
  assign: z.boolean(),
  start: z.boolean(),
  complete: z.boolean(),
  approve: z.boolean(),
  cancel: z.boolean(),
  delete: z.boolean(),
  force_delete: z.boolean(),
  comment: z.boolean(),
  manage_participants: z.boolean(),
  manage_dependencies: z.boolean(),
  /** Trocar de pipeline ou de coluna (mudança de categoria ainda passa pela ação de domínio). */
  move_pipeline: z.boolean()
});
var TaskApprovalSchema = z.object({
  id: z.uuid(),
  status: z.enum(["PENDING", "APPROVED", "REJECTED"]),
  requested_by: UserSummarySchema.nullable(),
  requested_at: z.iso.datetime(),
  reviewed_by: UserSummarySchema.nullable(),
  /** Decisão tomada pelo cliente no portal (Fase 7). */
  reviewed_by_contact: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  reviewed_at: z.iso.datetime().nullable(),
  comment: z.string().nullable()
});
var TaskDetailSchema = TaskSummarySchema.extend({
  description: z.string().nullable(),
  created_by: UserSummarySchema.nullable(),
  owner_user_id: z.uuid().nullable(),
  auto_complete_on_subtasks: z.boolean(),
  estimated_cost: z.string().nullable(),
  actual_cost: z.string().nullable(),
  parent: TaskRefSchema.nullable(),
  subtasks: z.array(TaskSummarySchema),
  dependencies: z.array(TaskDependencySchema),
  dependents: z.array(TaskDependencySchema),
  checklists: z.array(ChecklistSchema),
  approvals: z.array(TaskApprovalSchema),
  can: TaskAbilitiesSchema,
  deleted_at: z.iso.datetime().nullable(),
  external_request_id: z.uuid().nullable(),
  requester_contact: z.object({ id: z.uuid(), name: z.string() }).nullable()
});
var CreateTaskSchema = z.object({
  title: z.string().trim().min(1).max(300),
  description: z.string().max(5e4).optional(),
  scope: TaskScope.default("CORPORATE"),
  context_id: z.uuid().nullable().optional(),
  parent_id: z.uuid().nullable().optional(),
  responsible_user_id: z.uuid().nullable().optional(),
  participant_user_ids: z.array(z.uuid()).default([]),
  priority: TaskPriority.default("NORMAL"),
  due_at: z.iso.datetime().nullable().optional(),
  started_at: z.iso.datetime().nullable().optional(),
  requires_approval: z.boolean().default(false),
  visible_to_client: z.boolean().default(false),
  is_required: z.boolean().default(true),
  auto_complete_on_subtasks: z.boolean().default(false),
  estimated_minutes: z.number().int().min(0).nullable().optional(),
  estimated_cost: Money.nullable().optional(),
  tag_ids: z.array(z.uuid()).default([]),
  /** Pipeline manual (null/ausente = Geral) e coluna inicial (padrão: 1ª da categoria do status). */
  pipeline_id: z.uuid().nullable().optional(),
  pipeline_stage_id: z.uuid().nullable().optional()
});
var UpdateTaskSchema = z.object({
  title: z.string().trim().min(1).max(300).optional(),
  description: z.string().max(5e4).nullable().optional(),
  context_id: z.uuid().nullable().optional(),
  priority: TaskPriority.optional(),
  due_at: z.iso.datetime().nullable().optional(),
  started_at: z.iso.datetime().nullable().optional(),
  requires_approval: z.boolean().optional(),
  visible_to_client: z.boolean().optional(),
  is_required: z.boolean().optional(),
  auto_complete_on_subtasks: z.boolean().optional(),
  estimated_minutes: z.number().int().min(0).nullable().optional(),
  estimated_cost: Money.nullable().optional(),
  actual_cost: Money.nullable().optional(),
  tag_ids: z.array(z.uuid()).optional(),
  /** Troca de pipeline (null = volta para o Geral); a coluna vira a 1ª da categoria do status. */
  pipeline_id: z.uuid().nullable().optional()
});
var AssignTaskSchema = z.object({ responsible_user_id: z.uuid().nullable() });
var RequestApprovalSchema = z.object({
  comment: z.string().trim().max(2e3).optional(),
  by_client: z.boolean().default(false)
});
var CompleteTaskSchema = z.object({ comment: z.string().max(2e3).optional() });
var CancelTaskSchema = z.object({ reason: z.string().max(2e3).optional() });
var ApproveTaskSchema = z.object({ comment: z.string().max(2e3).optional() });
var RejectTaskSchema = z.object({ comment: z.string().trim().min(1).max(2e3) });
var ForceDeleteSchema = z.object({ confirmation: z.string().min(1) });
var AddParticipantSchema = z.object({ user_id: z.uuid() });
var AddDependencySchema = z.object({
  depends_on_task_id: z.uuid(),
  is_required: z.boolean().default(true)
});
var csv = (item) => z.preprocess((v) => typeof v === "string" ? v.split(",").filter(Boolean) : v, z.array(item)).optional();
var bool = z.union([z.boolean(), z.enum(["true", "false", "1", "0"])]).transform((v) => v === true || v === "true" || v === "1").optional();
var TaskSortField = z.enum([
  "due_at",
  "created_at",
  "updated_at",
  "completed_at",
  "priority",
  "status",
  "title",
  "number"
]);
var TaskListQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  scope: TaskScope.default("CORPORATE"),
  status: csv(TaskStatus),
  priority: csv(TaskPriority),
  responsible_user_id: z.uuid().optional(),
  participant_user_id: z.uuid().optional(),
  team_id: z.uuid().optional(),
  context_id: z.uuid().optional(),
  tag_id: z.uuid().optional(),
  workflow_stage_id: z.uuid().optional(),
  workflow_template_id: z.uuid().optional(),
  workflow_instance_id: z.uuid().optional(),
  /** Id de pipeline manual, ou "none" para as demandas sem pipeline (quadro Geral). */
  pipeline_id: z.union([z.uuid(), z.literal("none")]).optional(),
  parent_id: z.uuid().optional(),
  /** Só tarefas mãe (padrão true na lista; kanban e subtarefas passam false). */
  root_only: bool,
  due_from: z.iso.datetime().optional(),
  due_to: z.iso.datetime().optional(),
  created_from: z.iso.datetime().optional(),
  created_to: z.iso.datetime().optional(),
  completed_from: z.iso.datetime().optional(),
  completed_to: z.iso.datetime().optional(),
  overdue: bool,
  trashed: bool,
  sort: TaskSortField.default("due_at"),
  dir: z.enum(["asc", "desc"]).default("asc"),
  page: z.coerce.number().int().min(1).default(1),
  per_page: z.coerce.number().int().min(1).max(200).default(50)
});
var MyWorkSchema = z.object({
  overdue: z.array(TaskSummarySchema),
  today: z.array(TaskSummarySchema),
  week: z.array(TaskSummarySchema),
  pending_approval: z.array(TaskSummarySchema),
  assigned: z.array(TaskSummarySchema),
  personal: z.array(TaskSummarySchema),
  counts: z.object({
    overdue: z.number().int(),
    today: z.number().int(),
    week: z.number().int(),
    pending_approval: z.number().int(),
    assigned: z.number().int(),
    personal: z.number().int()
  })
});
var CreateChecklistSchema = z.object({ title: z.string().trim().min(1).max(120) });
var UpdateChecklistSchema = z.object({
  title: z.string().trim().min(1).max(120).optional(),
  sort_order: z.number().int().min(0).optional()
});
var CreateChecklistItemSchema = z.object({ text: z.string().trim().min(1).max(500) });
var UpdateChecklistItemSchema = z.object({
  text: z.string().trim().min(1).max(500).optional(),
  is_completed: z.boolean().optional(),
  sort_order: z.number().int().min(0).optional()
});
var ACTIVITY_ACTIONS = [
  "task.created",
  "task.updated",
  "task.assigned",
  "task.started",
  "task.completed",
  "task.canceled",
  "task.restored",
  "task.deleted",
  "task.approval_requested",
  "task.approved",
  "task.rejected",
  "task.participant_added",
  "task.participant_removed",
  "task.dependency_added",
  "task.dependency_removed",
  "task.dependency_unlocked",
  "task.stage_changed",
  "task.time_tracked",
  "workflow.started",
  "workflow.stage_completed",
  "workflow.completed",
  "workflow.override",
  "task.recurrence_generated",
  "automation.executed",
  "task.due_changed",
  "task.priority_changed",
  "comment.added",
  "comment.edited",
  "comment.deleted",
  "attachment.added",
  "attachment.removed",
  "checklist.item_completed"
];
var ActivityEntrySchema = z.object({
  id: z.uuid(),
  action: z.string(),
  actor: UserSummarySchema.nullable(),
  /** Ator externo (contato do cliente via portal). */
  actor_contact: z.object({ id: z.uuid(), name: z.string() }).nullable().optional(),
  payload: z.record(z.string(), z.unknown()).nullable(),
  created_at: z.iso.datetime()
});
var AttachmentEntityType = z.enum(["TASK", "COMMENT", "CLIENT"]);
var AttachmentSchema = z.object({
  id: z.uuid(),
  entity_type: AttachmentEntityType,
  entity_id: z.uuid(),
  filename: z.string(),
  mime_type: z.string(),
  size: z.number().int(),
  status: z.enum(["PENDING", "READY"]),
  uploaded_by: UserSummarySchema.nullable(),
  created_at: z.iso.datetime()
});
var MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;
var ALLOWED_ATTACHMENT_MIME = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "image/svg+xml",
  "application/pdf",
  "text/plain",
  "text/csv",
  "text/markdown",
  "application/zip",
  "application/json",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation"
];
var PresignAttachmentSchema = z.object({
  entity_type: AttachmentEntityType,
  entity_id: z.uuid(),
  filename: z.string().trim().min(1).max(200).regex(/^[^/\\]+$/, "Nome de arquivo inv\xE1lido."),
  mime_type: z.enum(ALLOWED_ATTACHMENT_MIME),
  size: z.number().int().min(1).max(MAX_ATTACHMENT_BYTES)
});
var PresignedUploadSchema = z.object({
  attachment: AttachmentSchema,
  upload_url: z.url(),
  headers: z.record(z.string(), z.string())
});
var CommentSchema = z.object({
  id: z.uuid(),
  parent_task_id: z.uuid(),
  origin_task: TaskRefSchema,
  author: UserSummarySchema.nullable(),
  /** Autor externo (contato do cliente via portal). */
  author_contact: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  visible_to_client: z.boolean(),
  content_markdown: z.string(),
  mentions: z.array(UserSummarySchema),
  attachments: z.array(AttachmentSchema),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
  is_edited: z.boolean()
});
var CreateCommentSchema = z.object({
  content_markdown: z.string().trim().min(1).max(2e4),
  mention_user_ids: z.array(z.uuid()).default([]),
  attachment_ids: z.array(z.uuid()).default([]),
  visible_to_client: z.boolean().default(false)
});
var UpdateCommentSchema = z.object({
  content_markdown: z.string().trim().min(1).max(2e4),
  mention_user_ids: z.array(z.uuid()).optional()
});
var FeedItemSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("comment"), comment: CommentSchema, created_at: z.iso.datetime() }),
  z.object({
    kind: z.literal("activity"),
    activity: ActivityEntrySchema,
    created_at: z.iso.datetime()
  })
]);
var NotificationType = z.enum([
  "TASK_ASSIGNED",
  "TASK_DUE_SOON",
  "TASK_OVERDUE",
  "TASK_COMPLETED",
  "TASK_APPROVAL_REQUESTED",
  "TASK_APPROVED",
  "TASK_REJECTED",
  "COMMENT_ADDED",
  "USER_MENTIONED",
  "DEPENDENCY_UNLOCKED",
  "WORKFLOW_STAGE_CHANGED",
  "WORKFLOW_COMPLETED",
  "CALENDAR_EVENT_REMINDER",
  "TASK_UNASSIGNED_ALERT",
  "AUTOMATION",
  "EXTERNAL_REQUEST_RECEIVED",
  "EXTERNAL_REQUEST_DECIDED"
]);
var NotificationSchema = z.object({
  id: z.uuid(),
  type: NotificationType,
  title: z.string(),
  body: z.string().nullable(),
  entity_type: z.string().nullable(),
  entity_id: z.uuid().nullable(),
  payload: z.record(z.string(), z.unknown()).nullable(),
  read_at: z.iso.datetime().nullable(),
  created_at: z.iso.datetime()
});
var NotificationListQuerySchema = z.object({
  unread: z.union([z.boolean(), z.enum(["true", "false"])]).transform((v) => v === true || v === "true").optional(),
  page: z.coerce.number().int().min(1).default(1),
  per_page: z.coerce.number().int().min(1).max(100).default(30)
});
var SavedViewSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  entity: z.string(),
  config: z.record(z.string(), z.unknown()),
  is_shared: z.boolean(),
  owner_user_id: z.uuid()
});
var CreateSavedViewSchema = z.object({
  name: z.string().trim().min(1).max(80),
  entity: z.enum(["TASKS"]).default("TASKS"),
  config: z.record(z.string(), z.unknown()),
  is_shared: z.boolean().default(false)
});
var UpdateSavedViewSchema = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  config: z.record(z.string(), z.unknown()).optional(),
  is_shared: z.boolean().optional()
});
var SlaUnit = z.enum(["HOURS", "DAYS", "BUSINESS_DAYS"]);
var HexColor2 = z.string().regex(/^#[0-9a-fA-F]{6}$/);
var ChecklistTemplateSchema = z.object({
  title: z.string().trim().min(1).max(120),
  items: z.array(z.string().trim().min(1).max(500)).default([])
});
var TaskTemplateInputSchema = z.object({
  title: z.string().trim().min(1).max(300),
  description: z.string().max(2e4).nullable().optional(),
  function_id: z.uuid().nullable().optional(),
  sla_value: z.number().int().min(0).nullable().optional(),
  sla_unit: SlaUnit.nullable().optional(),
  requires_approval: z.boolean().default(false),
  is_required: z.boolean().default(true),
  priority: TaskPriority.default("NORMAL"),
  checklists: z.array(ChecklistTemplateSchema).default([]),
  sort_order: z.number().int().min(0).optional()
});
var TaskTemplateSchema = z.object({
  id: z.uuid(),
  stage_template_id: z.uuid().nullable(),
  block_id: z.uuid().nullable(),
  source_block_id: z.uuid().nullable(),
  title: z.string(),
  description: z.string().nullable(),
  function: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  sla_value: z.number().int().nullable(),
  sla_unit: SlaUnit.nullable(),
  requires_approval: z.boolean(),
  is_required: z.boolean(),
  priority: TaskPriority,
  checklists: z.array(ChecklistTemplateSchema),
  sort_order: z.number().int(),
  /** Ids de templates dos quais depende (mesma etapa/bloco). */
  depends_on: z.array(z.uuid())
});
var TaskBlockSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  templates: z.array(TaskTemplateSchema)
});
var CreateTaskBlockSchema = z.object({
  name: z.string().trim().min(2).max(120),
  description: z.string().trim().max(1e3).optional()
});
var UpdateTaskBlockSchema = CreateTaskBlockSchema.partial();
var StageTemplateInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  color: HexColor2.nullable().optional(),
  manual_entry_allowed: z.boolean().default(true),
  manual_exit_allowed: z.boolean().default(true),
  auto_advance: z.boolean().default(true),
  manager_override_allowed: z.boolean().default(true),
  sort_order: z.number().int().min(0).optional()
});
var StageTemplateSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  color: z.string().nullable(),
  sort_order: z.number().int(),
  manual_entry_allowed: z.boolean(),
  manual_exit_allowed: z.boolean(),
  auto_advance: z.boolean(),
  manager_override_allowed: z.boolean(),
  templates: z.array(TaskTemplateSchema),
  blocks: z.array(z.object({ id: z.uuid(), name: z.string(), templates_count: z.number().int() }))
});
var WorkflowVersionSummarySchema = z.object({
  id: z.uuid(),
  version: z.number().int().nullable(),
  status: z.enum(["DRAFT", "PUBLISHED"]),
  notes: z.string().nullable(),
  published_at: z.iso.datetime().nullable(),
  created_by: UserSummarySchema.nullable(),
  stages_count: z.number().int(),
  instances_count: z.number().int()
});
var WorkflowVersionSchema = WorkflowVersionSummarySchema.extend({
  stages: z.array(StageTemplateSchema)
});
var WorkflowTemplateSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  status: z.enum(["ACTIVE", "ARCHIVED"]),
  draft: WorkflowVersionSummarySchema.nullable(),
  published: WorkflowVersionSummarySchema.nullable(),
  versions: z.array(WorkflowVersionSummarySchema),
  created_at: z.iso.datetime()
});
var CreateWorkflowTemplateSchema = z.object({
  name: z.string().trim().min(2).max(120),
  description: z.string().trim().max(2e3).optional()
});
var UpdateWorkflowTemplateSchema = CreateWorkflowTemplateSchema.partial().extend({
  status: z.enum(["ACTIVE", "ARCHIVED"]).optional()
});
var PublishWorkflowSchema = z.object({ notes: z.string().trim().max(2e3).optional() });
var TemplateDependencyInputSchema = z.object({
  depends_on_task_template_id: z.uuid(),
  is_required: z.boolean().default(true)
});
var StageInstanceSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  color: z.string().nullable(),
  sort_order: z.number().int(),
  status: z.enum(["PENDING", "ACTIVE", "COMPLETED", "SKIPPED"]),
  manual_entry_allowed: z.boolean(),
  manual_exit_allowed: z.boolean(),
  auto_advance: z.boolean(),
  manager_override_allowed: z.boolean(),
  entered_at: z.iso.datetime().nullable(),
  completed_at: z.iso.datetime().nullable(),
  tasks_total: z.number().int(),
  tasks_completed: z.number().int(),
  tasks_required_open: z.number().int()
});
var WorkflowInstanceSchema = z.object({
  id: z.uuid(),
  template: z.object({ id: z.uuid(), name: z.string() }),
  version: z.number().int().nullable(),
  status: z.enum(["ACTIVE", "COMPLETED", "CANCELED"]),
  parent_task: TaskRefSchema,
  context_id: z.uuid().nullable(),
  current_stage_id: z.uuid().nullable(),
  stages: z.array(StageInstanceSchema),
  started_at: z.iso.datetime(),
  completed_at: z.iso.datetime().nullable(),
  /** Habilidades do usuário atual sobre a instância. */
  can: z.object({ move: z.boolean(), override: z.boolean() })
});
var TaskWorkflowRefSchema = z.object({
  instance_id: z.uuid(),
  template_id: z.uuid(),
  template_name: z.string(),
  stage: z.object({
    id: z.uuid(),
    name: z.string(),
    color: z.string().nullable(),
    sort_order: z.number().int()
  }).nullable()
});
var StartWorkflowSchema = z.object({
  workflow_template_id: z.uuid(),
  context_id: z.uuid().nullable().optional(),
  title: z.string().trim().min(1).max(300).optional(),
  responsible_user_id: z.uuid().nullable().optional(),
  due_at: z.iso.datetime().nullable().optional(),
  priority: TaskPriority.default("NORMAL")
});
var MoveStageSchema = z.object({
  to_stage_instance_id: z.uuid().optional(),
  to_sort_order: z.number().int().min(0).optional(),
  justification: z.string().trim().max(2e3).optional()
});
var ContextFunctionAssignmentSchema = z.object({
  function: z.object({ id: z.uuid(), name: z.string() }),
  user: UserSummarySchema
});
var SetContextFunctionSchema = z.object({
  function_id: z.uuid(),
  user_id: z.uuid().nullable()
});
var HolidaySchema = z.object({ id: z.uuid(), date: z.iso.date(), name: z.string() });
var CreateHolidaySchema = z.object({
  date: z.iso.date(),
  name: z.string().trim().min(1).max(120)
});
var RecurrenceRuleSchema = z.object({
  id: z.uuid(),
  entity_type: z.string(),
  entity_id: z.uuid(),
  rrule: z.string(),
  timezone: z.string(),
  business_day_adjust: z.boolean(),
  next_run_at: z.iso.datetime().nullable(),
  last_run_at: z.iso.datetime().nullable(),
  status: z.enum(["ACTIVE", "PAUSED", "ENDED"]),
  /** Próximas 5 ocorrências para o usuário conferir. */
  preview: z.array(z.iso.datetime()),
  generated_count: z.number().int()
});
var SetRecurrenceSchema = z.object({
  rrule: z.string().trim().min(6).max(500),
  timezone: z.string().trim().min(1).max(64).default("America/Sao_Paulo"),
  business_day_adjust: z.boolean().default(false),
  status: z.enum(["ACTIVE", "PAUSED"]).default("ACTIVE")
});
var AutomationTrigger = z.enum([
  "TASK_CREATED",
  "TASK_COMPLETED",
  "TASK_APPROVED",
  "TASK_REJECTED",
  "STAGE_COMPLETED",
  "CONTRACT_ACTIVATED",
  "EXTERNAL_REQUEST_APPROVED",
  "DUE_DATE_REACHED"
]);
var AutomationActionSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("CREATE_TASK"),
    params: z.object({
      title: z.string().min(1).max(300),
      description: z.string().max(5e3).optional(),
      function_id: z.uuid().nullable().optional(),
      responsible_user_id: z.uuid().nullable().optional(),
      priority: TaskPriority.default("NORMAL"),
      sla_value: z.number().int().min(0).optional(),
      sla_unit: SlaUnit.optional(),
      as_subtask: z.boolean().default(false)
    })
  }),
  z.object({
    type: z.literal("CREATE_TASK_BLOCK"),
    params: z.object({ block_id: z.uuid(), as_subtasks: z.boolean().default(true) })
  }),
  z.object({
    type: z.literal("MOVE_STAGE"),
    params: z.object({ to_sort_order: z.number().int().min(0) })
  }),
  z.object({
    type: z.literal("ASSIGN_USER"),
    params: z.object({ user_id: z.uuid().nullable(), function_id: z.uuid().nullable().optional() })
  }),
  z.object({
    type: z.literal("SEND_NOTIFICATION"),
    params: z.object({
      to: z.enum(["RESPONSIBLE", "PARTICIPANTS", "CREATOR", "MANAGERS", "USER"]),
      user_id: z.uuid().optional(),
      title: z.string().min(1).max(200),
      body: z.string().max(1e3).optional()
    })
  }),
  z.object({ type: z.literal("SET_TAG"), params: z.object({ tag_id: z.uuid() }) }),
  z.object({
    type: z.literal("CREATE_WORKFLOW"),
    params: z.object({ workflow_template_id: z.uuid(), title: z.string().max(300).optional() })
  })
]);
var AutomationConditionsSchema = z.object({
  workflow_template_id: z.uuid().nullable().optional(),
  context_id: z.uuid().nullable().optional(),
  tag_id: z.uuid().nullable().optional(),
  priority: TaskPriority.nullable().optional(),
  stage_name: z.string().max(120).nullable().optional(),
  /** DUE_DATE_REACHED: disparar N horas antes (0 = no prazo). */
  hours_before: z.number().int().min(0).max(720).nullable().optional()
});
var AutomationRuleSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  trigger: AutomationTrigger,
  conditions: AutomationConditionsSchema,
  actions: z.array(AutomationActionSchema),
  status: z.enum(["ACTIVE", "PAUSED"]),
  runs_count: z.number().int(),
  created_at: z.iso.datetime()
});
var CreateAutomationRuleSchema = z.object({
  name: z.string().trim().min(2).max(120),
  trigger: AutomationTrigger,
  conditions: AutomationConditionsSchema.default({}),
  actions: z.array(AutomationActionSchema).min(1),
  status: z.enum(["ACTIVE", "PAUSED"]).default("ACTIVE")
});
var UpdateAutomationRuleSchema = z.object({
  name: z.string().trim().min(2).max(120).optional(),
  trigger: AutomationTrigger.optional(),
  conditions: AutomationConditionsSchema.optional(),
  actions: z.array(AutomationActionSchema).min(1).max(10).optional(),
  status: z.enum(["ACTIVE", "PAUSED"]).optional()
});
var TaskSummaryWithWorkflowSchema = TaskSummarySchema.extend({
  workflow: TaskWorkflowRefSchema.nullable()
});
var FormAccessMode = z.enum(["PUBLIC_URL", "TOKEN_PROTECTED", "AUTHENTICATED_CLIENT"]);
var FormStatus = z.enum(["ACTIVE", "INACTIVE", "ARCHIVED"]);
var FormFieldType = z.enum([
  "TEXT",
  "TEXTAREA",
  "NUMBER",
  "CURRENCY",
  "DATE",
  "DATETIME",
  "BOOLEAN",
  "SELECT",
  "MULTISELECT",
  "URL",
  "EMAIL",
  "PHONE"
]);
var FieldName = z.string().trim().min(1).max(60).regex(/^[a-z][a-z0-9_]*$/, "Use letras min\xFAsculas, n\xFAmeros e _ (come\xE7ando por letra).");
var RESERVED_FIELD_NAMES = /* @__PURE__ */ new Set(["priority", "prioridade", "requester", "status"]);
var FormFieldInputSchema = z.object({
  id: z.uuid().optional(),
  field_type: FormFieldType,
  label: z.string().trim().min(1).max(160),
  name: FieldName.optional(),
  required: z.boolean().default(false),
  options: z.array(z.string().trim().min(1).max(120)).max(50).default([]),
  placeholder: z.string().trim().max(160).nullable().optional(),
  help_text: z.string().trim().max(500).nullable().optional()
}).superRefine((v, ctx) => {
  if (v.name && RESERVED_FIELD_NAMES.has(v.name)) {
    ctx.addIssue({ code: "custom", path: ["name"], message: "Nome reservado." });
  }
  if ((v.field_type === "SELECT" || v.field_type === "MULTISELECT") && v.options.length === 0) {
    ctx.addIssue({ code: "custom", path: ["options"], message: "Informe ao menos uma op\xE7\xE3o." });
  }
});
var FormFieldSchema = z.object({
  id: z.uuid(),
  field_type: FormFieldType,
  label: z.string(),
  name: z.string(),
  required: z.boolean(),
  options: z.array(z.string()),
  placeholder: z.string().nullable(),
  help_text: z.string().nullable(),
  sort_order: z.number().int()
});
var FormClientRefSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  context_id: z.uuid()
});
var FormSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  slug: z.string(),
  description: z.string().nullable(),
  access_mode: FormAccessMode,
  status: FormStatus,
  client: FormClientRefSchema.nullable(),
  success_message: z.string().nullable(),
  /** Token existe (nunca é devolvido depois de gerado). */
  has_token: z.boolean(),
  fields: z.array(FormFieldSchema),
  requests_count: z.number().int(),
  pending_count: z.number().int(),
  created_by: UserSummarySchema.nullable(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime()
});
var CreateFormSchema = z.object({
  name: z.string().trim().min(2).max(120),
  description: z.string().trim().max(2e3).nullable().optional(),
  access_mode: FormAccessMode.default("PUBLIC_URL"),
  client_id: z.uuid().nullable().optional(),
  success_message: z.string().trim().max(1e3).nullable().optional(),
  fields: z.array(FormFieldInputSchema).max(60).default([])
});
var UpdateFormSchema = z.object({
  name: z.string().trim().min(2).max(120).optional(),
  description: z.string().trim().max(2e3).nullable().optional(),
  access_mode: FormAccessMode.optional(),
  status: FormStatus.optional(),
  client_id: z.uuid().nullable().optional(),
  success_message: z.string().trim().max(1e3).nullable().optional()
});
var SetFormFieldsSchema = z.object({ fields: z.array(FormFieldInputSchema).max(60) });
var FormTokenSchema = z.object({ token: z.string(), url_hint: z.string() });
var PublicFormSchema = z.object({
  name: z.string(),
  description: z.string().nullable(),
  organization_name: z.string(),
  access_mode: FormAccessMode,
  fields: z.array(FormFieldSchema)
});
var RequesterInputSchema = z.object({
  name: z.string().trim().min(2).max(160),
  email: z.email().max(254),
  phone: z.string().trim().max(40).nullable().optional()
});
var SubmitFormSchema = z.object({
  requester: RequesterInputSchema,
  answers: z.record(z.string(), z.unknown()).default({})
});
var SubmitFormAuthenticatedSchema = SubmitFormSchema.extend({
  contact_id: z.uuid().nullable().optional()
});
var SubmitResultSchema = z.object({
  request_id: z.uuid(),
  message: z.string()
});
var ExternalRequestStatus = z.enum(["NEW", "TRIAGE", "APPROVED", "REJECTED"]);
var RequestAnswerSchema = z.object({
  name: z.string(),
  label: z.string(),
  field_type: FormFieldType,
  value: z.unknown()
});
var ExternalRequestSchema = z.object({
  id: z.uuid(),
  form: z.object({ id: z.uuid(), name: z.string(), slug: z.string() }),
  status: ExternalRequestStatus,
  title: z.string(),
  client: FormClientRefSchema.nullable(),
  requester: z.object({
    name: z.string(),
    email: z.string(),
    phone: z.string().nullable(),
    contact: z.object({ id: z.uuid(), name: z.string() }).nullable()
  }),
  answers: z.array(RequestAnswerSchema),
  created_task: TaskRefSchema.nullable(),
  submitted_by: UserSummarySchema.nullable(),
  triaged_by: UserSummarySchema.nullable(),
  triaged_at: z.iso.datetime().nullable(),
  decided_at: z.iso.datetime().nullable(),
  decision_note: z.string().nullable(),
  created_at: z.iso.datetime()
});
var ExternalRequestListQuerySchema = z.object({
  status: z.preprocess(
    (v) => typeof v === "string" ? v.split(",").filter(Boolean) : v,
    z.array(ExternalRequestStatus)
  ).optional(),
  form_id: z.uuid().optional(),
  q: z.string().trim().max(120).optional(),
  page: z.coerce.number().int().min(1).default(1),
  per_page: z.coerce.number().int().min(1).max(100).default(30)
});
var ApproveExternalRequestSchema = z.object({
  title: z.string().trim().min(1).max(300).optional(),
  description: z.string().max(5e4).optional(),
  context_id: z.uuid().nullable().optional(),
  responsible_user_id: z.uuid().nullable().optional(),
  priority: TaskPriority.default("NORMAL"),
  due_at: z.iso.datetime().nullable().optional(),
  workflow_template_id: z.uuid().nullable().optional(),
  tag_ids: z.array(z.uuid()).default([]),
  requires_approval: z.boolean().default(false),
  visible_to_client: z.boolean().default(true),
  /** Cria o contato no cliente quando o solicitante ainda não existe. */
  create_contact: z.boolean().default(true),
  note: z.string().trim().max(2e3).nullable().optional()
});
var RejectExternalRequestSchema = z.object({
  reason: z.string().trim().min(2).max(2e3)
});
var ExternalRequestCountsSchema = z.object({
  NEW: z.number().int(),
  TRIAGE: z.number().int(),
  APPROVED: z.number().int(),
  REJECTED: z.number().int()
});
var TimeEntrySource = z.enum(["TIMER", "MANUAL"]);
var TimeEntrySchema = z.object({
  id: z.uuid(),
  task: TaskRefSchema,
  user: UserSummarySchema,
  started_at: z.iso.datetime(),
  ended_at: z.iso.datetime().nullable(),
  /** null enquanto o cronômetro roda. */
  duration_seconds: z.number().int().nullable(),
  description: z.string().nullable(),
  source: TimeEntrySource,
  running: z.boolean()
});
var StartTimerSchema = z.object({
  description: z.string().trim().max(500).nullable().optional()
});
var StopTimerSchema = z.object({
  description: z.string().trim().max(500).nullable().optional()
});
var ManualTimeEntrySchema = z.object({
  started_at: z.iso.datetime(),
  ended_at: z.iso.datetime().optional(),
  duration_minutes: z.number().int().min(1).max(24 * 60).optional(),
  description: z.string().trim().max(500).nullable().optional()
}).refine((v) => v.ended_at || v.duration_minutes, {
  message: "Informe o fim ou a dura\xE7\xE3o.",
  path: ["duration_minutes"]
});
var TaskTimeSchema = z.object({
  entries: z.array(TimeEntrySchema),
  total_seconds: z.number().int(),
  /** Cronômetro do usuário atual nesta tarefa, se rodando. */
  running: TimeEntrySchema.nullable()
});
var CustomFieldEntity = z.enum(["TASK", "CLIENT"]);
var CustomFieldType = z.enum([
  "TEXT",
  "TEXTAREA",
  "NUMBER",
  "CURRENCY",
  "DATE",
  "DATETIME",
  "BOOLEAN",
  "SELECT",
  "MULTISELECT",
  "USER",
  "CLIENT",
  "URL",
  "EMAIL",
  "PHONE"
]);
var CustomFieldDefinitionSchema = z.object({
  id: z.uuid(),
  entity_type: CustomFieldEntity,
  field_type: CustomFieldType,
  name: z.string(),
  key: z.string(),
  required: z.boolean(),
  options: z.array(z.string()),
  help_text: z.string().nullable(),
  sort_order: z.number().int()
});
var CreateCustomFieldSchema = z.object({
  entity_type: CustomFieldEntity,
  field_type: CustomFieldType,
  name: z.string().trim().min(1).max(120),
  key: z.string().trim().regex(/^[a-z][a-z0-9_]*$/).max(60).optional(),
  required: z.boolean().default(false),
  options: z.array(z.string().trim().min(1).max(120)).max(50).default([]),
  help_text: z.string().trim().max(500).nullable().optional()
}).superRefine((v, ctx) => {
  if ((v.field_type === "SELECT" || v.field_type === "MULTISELECT") && v.options.length === 0) {
    ctx.addIssue({ code: "custom", path: ["options"], message: "Informe ao menos uma op\xE7\xE3o." });
  }
});
var UpdateCustomFieldSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  required: z.boolean().optional(),
  options: z.array(z.string().trim().min(1).max(120)).max(50).optional(),
  help_text: z.string().trim().max(500).nullable().optional(),
  sort_order: z.number().int().min(0).optional()
});
var CustomFieldValueSchema = z.object({
  definition: CustomFieldDefinitionSchema,
  value: z.unknown(),
  /** Rótulo resolvido para USER/CLIENT. */
  display: z.string().nullable()
});
var SetCustomFieldValuesSchema = z.object({
  values: z.record(z.string(), z.unknown())
});
var CalendarEventSchema = z.object({
  id: z.uuid(),
  /** Id da série; ocorrências expandidas repetem o id com `occurrence_start`. */
  title: z.string(),
  description: z.string().nullable(),
  starts_at: z.iso.datetime(),
  ends_at: z.iso.datetime(),
  all_day: z.boolean(),
  timezone: z.string(),
  location: z.string().nullable(),
  meeting_url: z.string().nullable(),
  context: z.object({ id: z.uuid(), name: z.string(), type: ContextType }).nullable(),
  rrule: z.string().nullable(),
  participants: z.array(UserSummarySchema),
  created_by: UserSummarySchema.nullable(),
  /** Presente quando o evento é uma ocorrência de série. */
  occurrence: z.boolean(),
  can_edit: z.boolean()
});
var CreateCalendarEventSchema = z.object({
  title: z.string().trim().min(1).max(200),
  description: z.string().max(1e4).nullable().optional(),
  starts_at: z.iso.datetime(),
  ends_at: z.iso.datetime(),
  all_day: z.boolean().default(false),
  timezone: z.string().max(64).default("America/Sao_Paulo"),
  location: z.string().trim().max(300).nullable().optional(),
  meeting_url: z.url().max(2048).nullable().optional(),
  context_id: z.uuid().nullable().optional(),
  rrule: z.string().trim().max(500).nullable().optional(),
  participant_user_ids: z.array(z.uuid()).default([]),
  /** Times expandem para seus membros (SPEC §34). */
  team_ids: z.array(z.uuid()).default([])
}).refine((v) => new Date(v.ends_at) > new Date(v.starts_at), {
  message: "O fim deve ser depois do in\xEDcio.",
  path: ["ends_at"]
});
var UpdateCalendarEventSchema = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  description: z.string().max(1e4).nullable().optional(),
  starts_at: z.iso.datetime().optional(),
  ends_at: z.iso.datetime().optional(),
  all_day: z.boolean().optional(),
  timezone: z.string().max(64).optional(),
  location: z.string().trim().max(300).nullable().optional(),
  meeting_url: z.url().max(2048).nullable().optional(),
  context_id: z.uuid().nullable().optional(),
  rrule: z.string().trim().max(500).nullable().optional(),
  participant_user_ids: z.array(z.uuid()).optional(),
  team_ids: z.array(z.uuid()).optional()
});
var CalendarQuerySchema = z.object({
  from: z.iso.datetime(),
  to: z.iso.datetime(),
  context_id: z.uuid().optional(),
  /** Só eventos em que participo (padrão: todos visíveis da organização). */
  mine: z.union([z.boolean(), z.enum(["true", "false"])]).transform((v) => v === true || v === "true").optional(),
  /** Inclui prazos de tarefas como itens do calendário. */
  include_tasks: z.union([z.boolean(), z.enum(["true", "false"])]).transform((v) => v === true || v === "true").optional()
});
var CalendarRangeSchema = z.object({
  events: z.array(CalendarEventSchema),
  tasks: z.array(TaskSummarySchema)
});
var ReportPeriodSchema = z.object({
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
  context_id: z.uuid().optional(),
  team_id: z.uuid().optional()
});
var NamedCount = z.object({ id: z.uuid().nullable(), name: z.string(), count: z.number().int() });
var DashboardSchema = z.object({
  period: z.object({ from: z.iso.datetime(), to: z.iso.datetime() }),
  cards: z.object({
    pending: z.number().int(),
    overdue: z.number().int(),
    not_started: z.number().int(),
    completed_in_period: z.number().int(),
    completed_on_time_pct: z.number().nullable(),
    unassigned: z.number().int(),
    pending_approval: z.number().int()
  }),
  open_by_user: z.array(NamedCount),
  completed_by_user: z.array(NamedCount),
  completed_by_team: z.array(NamedCount),
  tasks_by_client: z.array(NamedCount),
  top_clients_open: z.array(NamedCount),
  by_status: z.array(z.object({ status: TaskStatus, count: z.number().int() })),
  by_priority: z.array(z.object({ priority: TaskPriority, count: z.number().int() })),
  /** 1=segunda … 5=sexta (prazos de tarefas abertas no período). */
  deliveries_by_weekday: z.array(z.object({ weekday: z.number().int(), count: z.number().int() }))
});
var WeeklyQuerySchema = z.object({
  /** Qualquer data da semana desejada (ISO); padrão: hoje. */
  date: z.iso.datetime().optional(),
  context_id: z.uuid().optional(),
  responsible_user_id: z.uuid().optional()
});
var WeeklySchema = z.object({
  week_start: z.iso.datetime(),
  days: z.array(
    z.object({
      date: z.string(),
      weekday: z.number().int(),
      count: z.number().int(),
      overdue: z.number().int(),
      completed: z.number().int(),
      tasks: z.array(TaskSummarySchema)
    })
  )
});
var ReportFormat = z.enum(["json", "csv"]);
var ReportQuerySchema = ReportPeriodSchema.extend({
  format: ReportFormat.default("json"),
  status: z.preprocess(
    (v) => typeof v === "string" ? v.split(",").filter(Boolean) : v,
    z.array(TaskStatus)
  ).optional()
});
var ReportRowSchema = z.record(z.string(), z.union([z.string(), z.number(), z.null()]));
var ReportSchema = z.object({
  columns: z.array(z.object({ key: z.string(), label: z.string() })),
  rows: z.array(ReportRowSchema)
});
var NotificationChannel = z.enum(["IN_APP", "EMAIL", "WHATSAPP", "TELEGRAM"]);
var PREFERENCE_EVENTS = [
  "TASK_ASSIGNED",
  "TASK_DUE_SOON",
  "TASK_OVERDUE",
  "TASK_COMPLETED",
  "TASK_APPROVAL_REQUESTED",
  "TASK_APPROVED",
  "TASK_REJECTED",
  "COMMENT_ADDED",
  "USER_MENTIONED",
  "DEPENDENCY_UNLOCKED",
  "WORKFLOW_STAGE_CHANGED",
  "WORKFLOW_COMPLETED",
  "CALENDAR_EVENT_REMINDER",
  "TASK_UNASSIGNED_ALERT",
  "EXTERNAL_REQUEST_RECEIVED",
  "EXTERNAL_REQUEST_DECIDED",
  "AUTOMATION"
];
var PREFERENCE_EVENT_LABEL = {
  TASK_ASSIGNED: "Tarefa atribu\xEDda a mim",
  TASK_DUE_SOON: "Prazo em menos de 24h",
  TASK_OVERDUE: "Tarefa atrasada",
  TASK_COMPLETED: "Tarefa conclu\xEDda",
  TASK_APPROVAL_REQUESTED: "Aprova\xE7\xE3o solicitada",
  TASK_APPROVED: "Tarefa aprovada",
  TASK_REJECTED: "Tarefa reprovada",
  COMMENT_ADDED: "Novo coment\xE1rio",
  USER_MENTIONED: "Men\xE7\xE3o a mim",
  DEPENDENCY_UNLOCKED: "Depend\xEAncia liberada",
  WORKFLOW_STAGE_CHANGED: "Etapa avan\xE7ada",
  WORKFLOW_COMPLETED: "Workflow conclu\xEDdo",
  CALENDAR_EVENT_REMINDER: "Reuni\xE3o pr\xF3xima / convite",
  TASK_UNASSIGNED_ALERT: "Tarefa sem respons\xE1vel",
  EXTERNAL_REQUEST_RECEIVED: "Nova solicita\xE7\xE3o externa",
  EXTERNAL_REQUEST_DECIDED: "Solicita\xE7\xE3o decidida",
  AUTOMATION: "Automa\xE7\xF5es"
};
var ChannelFlagsSchema = z.object({
  in_app: z.boolean(),
  email: z.boolean(),
  whatsapp: z.boolean(),
  telegram: z.boolean()
});
var DEFAULT_PREFERENCES = {
  TASK_ASSIGNED: { in_app: true, email: true, whatsapp: false, telegram: false },
  TASK_DUE_SOON: { in_app: true, email: true, whatsapp: false, telegram: false },
  TASK_OVERDUE: { in_app: true, email: true, whatsapp: false, telegram: false },
  TASK_COMPLETED: { in_app: true, email: false, whatsapp: false, telegram: false },
  TASK_APPROVAL_REQUESTED: { in_app: true, email: true, whatsapp: false, telegram: false },
  TASK_APPROVED: { in_app: true, email: false, whatsapp: false, telegram: false },
  TASK_REJECTED: { in_app: true, email: true, whatsapp: false, telegram: false },
  COMMENT_ADDED: { in_app: true, email: false, whatsapp: false, telegram: false },
  USER_MENTIONED: { in_app: true, email: true, whatsapp: false, telegram: false },
  DEPENDENCY_UNLOCKED: { in_app: true, email: false, whatsapp: false, telegram: false },
  WORKFLOW_STAGE_CHANGED: { in_app: true, email: false, whatsapp: false, telegram: false },
  WORKFLOW_COMPLETED: { in_app: true, email: false, whatsapp: false, telegram: false },
  CALENDAR_EVENT_REMINDER: { in_app: true, email: false, whatsapp: false, telegram: false },
  TASK_UNASSIGNED_ALERT: { in_app: true, email: true, whatsapp: false, telegram: false },
  EXTERNAL_REQUEST_RECEIVED: { in_app: true, email: true, whatsapp: false, telegram: false },
  EXTERNAL_REQUEST_DECIDED: { in_app: true, email: false, whatsapp: false, telegram: false },
  AUTOMATION: { in_app: true, email: false, whatsapp: false, telegram: false }
};
var NotificationPreferenceSchema = z.object({
  event_type: z.enum(PREFERENCE_EVENTS),
  ...ChannelFlagsSchema.shape
});
var SetNotificationPreferencesSchema = z.object({
  items: z.array(NotificationPreferenceSchema).min(1)
});
var ChannelStatusSchema = z.object({
  email: z.object({ address: z.string().nullable(), available: z.boolean() }),
  whatsapp: z.object({
    /** Número informado no canal (tem prioridade). */
    number: z.string().nullable(),
    /** Sem número no canal, as mensagens vão para o telefone do perfil (já em formato WhatsApp). */
    profile_number: z.string().nullable(),
    available: z.boolean()
  }),
  telegram: z.object({
    linked: z.boolean(),
    chat_id: z.string().nullable(),
    bot_username: z.string().nullable(),
    available: z.boolean()
  })
});
var SetWhatsAppNumberSchema = z.object({
  number: z.string().trim().regex(/^\+?[0-9]{10,15}$/, "Use o formato internacional, ex.: +5511999990000.").nullable()
});
var TelegramLinkCodeSchema = z.object({
  code: z.string(),
  expires_at: z.iso.datetime(),
  bot_username: z.string().nullable(),
  deep_link: z.string().nullable()
});
var DeliveryChannel = z.enum(["EMAIL", "WHATSAPP", "TELEGRAM", "CALENDAR"]);
var DeliveryStatus = z.enum(["PENDING", "SENT", "FAILED", "DEAD", "SKIPPED"]);
var IntegrationDeliverySchema = z.object({
  id: z.uuid(),
  channel: DeliveryChannel,
  status: DeliveryStatus,
  target: z.string().nullable(),
  subject: z.string().nullable(),
  attempts: z.number().int(),
  last_error: z.string().nullable(),
  user: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  notification_type: z.string().nullable(),
  created_at: z.iso.datetime(),
  sent_at: z.iso.datetime().nullable()
});
var DeliveryListQuerySchema = z.object({
  status: z.preprocess(
    (v) => typeof v === "string" ? v.split(",").filter(Boolean) : v,
    z.array(DeliveryStatus)
  ).optional(),
  channel: DeliveryChannel.optional(),
  page: z.coerce.number().int().min(1).default(1),
  per_page: z.coerce.number().int().min(1).max(100).default(30)
});
var CalendarProvider = z.enum(["GOOGLE", "MICROSOFT", "CALDAV"]);
var CalendarIntegrationStatus = z.enum(["ACTIVE", "ERROR", "DISABLED"]);
var CalendarIntegrationSchema = z.object({
  id: z.uuid(),
  provider: CalendarProvider,
  status: CalendarIntegrationStatus,
  external_calendar_id: z.string().nullable(),
  account_label: z.string().nullable(),
  last_synced_at: z.iso.datetime().nullable(),
  last_error: z.string().nullable(),
  created_at: z.iso.datetime()
});
var CalendarIntegrationsStateSchema = z.object({
  available: z.object({ GOOGLE: z.boolean(), MICROSOFT: z.boolean(), CALDAV: z.boolean() }),
  items: z.array(CalendarIntegrationSchema)
});
var ConnectCalDavSchema = z.object({
  /** URL da coleção do calendário (ex.: https://cal.example.com/dav/user/calendars/default/). */
  url: z.url().max(2048),
  username: z.string().trim().min(1).max(200),
  password: z.string().min(1).max(500),
  account_label: z.string().trim().max(120).optional()
});
var OAuthStartSchema = z.object({ url: z.url() });
var IntegrationsHealthSchema = z.object({
  email: z.boolean(),
  whatsapp: z.boolean(),
  telegram: z.boolean(),
  calendar: z.object({ GOOGLE: z.boolean(), MICROSOFT: z.boolean(), CALDAV: z.boolean() })
});
var Money2 = z.union([z.number(), z.string()]).transform((v) => typeof v === "number" ? v.toFixed(2) : v).pipe(z.string().regex(/^\d+(\.\d{1,2})?$/, "Valor monet\xE1rio inv\xE1lido."));
var Qty = z.union([z.number(), z.string()]).transform((v) => typeof v === "number" ? String(v) : v).pipe(z.string().regex(/^\d+(\.\d{1,3})?$/, "Quantidade inv\xE1lida."));
var ClientRef = z.object({ id: z.uuid(), name: z.string(), context_id: z.uuid() });
var ServiceStatus = z.enum(["ACTIVE", "INACTIVE"]);
var ServiceSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  category: z.string().nullable(),
  base_price: z.string().nullable(),
  status: ServiceStatus,
  workflow_templates: z.array(z.object({ id: z.uuid(), name: z.string() })),
  task_blocks: z.array(z.object({ id: z.uuid(), name: z.string() })),
  created_at: z.iso.datetime()
});
var CreateServiceSchema = z.object({
  name: z.string().trim().min(2).max(160),
  description: z.string().trim().max(5e3).nullable().optional(),
  category: z.string().trim().max(80).nullable().optional(),
  base_price: Money2.nullable().optional(),
  status: ServiceStatus.default("ACTIVE"),
  workflow_template_ids: z.array(z.uuid()).default([]),
  task_block_ids: z.array(z.uuid()).default([])
});
var UpdateServiceSchema = z.object({
  name: z.string().trim().min(2).max(160).optional(),
  description: z.string().trim().max(5e3).nullable().optional(),
  category: z.string().trim().max(80).nullable().optional(),
  base_price: Money2.nullable().optional(),
  status: ServiceStatus.optional(),
  workflow_template_ids: z.array(z.uuid()).optional(),
  task_block_ids: z.array(z.uuid()).optional()
});
var ProposalStatus = z.enum(["DRAFT", "SENT", "APPROVED", "REJECTED", "EXPIRED"]);
var ProposalItemSchema = z.object({
  id: z.uuid(),
  service: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  description: z.string(),
  quantity: z.string(),
  unit_price: z.string(),
  total: z.string(),
  sort_order: z.number().int()
});
var ProposalItemInputSchema = z.object({
  service_id: z.uuid().nullable().optional(),
  /** Vazio = usa nome/descrição do serviço no momento (snapshot). */
  description: z.string().trim().max(2e3).optional(),
  quantity: Qty.default("1"),
  /** Vazio = preço base do serviço no momento (snapshot). */
  unit_price: Money2.optional()
});
var ProposalSchema = z.object({
  id: z.uuid(),
  number: z.number().int(),
  client: ClientRef,
  status: ProposalStatus,
  valid_until: z.string().nullable(),
  total: z.string(),
  notes: z.string().nullable(),
  items: z.array(ProposalItemSchema),
  contract_id: z.uuid().nullable(),
  created_by: UserSummarySchema.nullable(),
  decided_by: UserSummarySchema.nullable(),
  decided_at: z.iso.datetime().nullable(),
  sent_at: z.iso.datetime().nullable(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime()
});
var CreateProposalSchema = z.object({
  client_id: z.uuid(),
  valid_until: z.iso.date().nullable().optional(),
  notes: z.string().trim().max(1e4).nullable().optional(),
  items: z.array(ProposalItemInputSchema).max(100).default([])
});
var UpdateProposalSchema = z.object({
  valid_until: z.iso.date().nullable().optional(),
  notes: z.string().trim().max(1e4).nullable().optional(),
  items: z.array(ProposalItemInputSchema).max(100).optional()
});
var ApproveProposalSchema = z.object({
  create_contract: z.boolean().default(true),
  start_date: z.iso.date().optional(),
  end_date: z.iso.date().nullable().optional(),
  note: z.string().trim().max(2e3).nullable().optional()
});
var RejectProposalSchema = z.object({ reason: z.string().trim().min(2).max(2e3) });
var ProposalListQuerySchema = z.object({
  status: z.preprocess(
    (v) => typeof v === "string" ? v.split(",").filter(Boolean) : v,
    z.array(ProposalStatus)
  ).optional(),
  client_id: z.uuid().optional(),
  q: z.string().trim().max(120).optional(),
  page: z.coerce.number().int().min(1).default(1),
  per_page: z.coerce.number().int().min(1).max(100).default(30)
});
var ContractStatus = z.enum(["DRAFT", "ACTIVE", "SUSPENDED", "ENDED", "CANCELED"]);
var ContractServiceSchema = z.object({
  id: z.uuid(),
  service: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  service_name: z.string(),
  quantity: z.string(),
  price: z.string(),
  /** Demanda-mãe do workflow iniciado na ativação, se houver. */
  task: TaskRefSchema.nullable()
});
var ContractServiceInputSchema = z.object({
  service_id: z.uuid().nullable().optional(),
  service_name: z.string().trim().max(200).optional(),
  quantity: Qty.default("1"),
  price: Money2.optional()
});
var ContractSchema = z.object({
  id: z.uuid(),
  number: z.number().int(),
  client: ClientRef,
  proposal: z.object({ id: z.uuid(), number: z.number().int() }).nullable(),
  status: ContractStatus,
  start_date: z.string(),
  end_date: z.string().nullable(),
  total: z.string(),
  notes: z.string().nullable(),
  services: z.array(ContractServiceSchema),
  activated_at: z.iso.datetime().nullable(),
  activated_by: UserSummarySchema.nullable(),
  created_by: UserSummarySchema.nullable(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime()
});
var CreateContractSchema = z.object({
  client_id: z.uuid(),
  proposal_id: z.uuid().nullable().optional(),
  start_date: z.iso.date(),
  end_date: z.iso.date().nullable().optional(),
  notes: z.string().trim().max(1e4).nullable().optional(),
  services: z.array(ContractServiceInputSchema).max(100).default([])
});
var UpdateContractSchema = z.object({
  start_date: z.iso.date().optional(),
  end_date: z.iso.date().nullable().optional(),
  notes: z.string().trim().max(1e4).nullable().optional(),
  services: z.array(ContractServiceInputSchema).max(100).optional()
});
var ActivateContractSchema = z.object({
  start_workflows: z.boolean().default(true),
  responsible_user_id: z.uuid().nullable().optional()
});
var ContractListQuerySchema = z.object({
  status: z.preprocess(
    (v) => typeof v === "string" ? v.split(",").filter(Boolean) : v,
    z.array(ContractStatus)
  ).optional(),
  client_id: z.uuid().optional(),
  q: z.string().trim().max(120).optional(),
  page: z.coerce.number().int().min(1).default(1),
  per_page: z.coerce.number().int().min(1).max(100).default(30)
});
var ContextNoteSchema = z.object({
  context_id: z.uuid(),
  content_markdown: z.string(),
  updated_by: UserSummarySchema.nullable(),
  updated_at: z.iso.datetime().nullable()
});
var SetContextNoteSchema = z.object({ content_markdown: z.string().max(2e5) });
var CredentialSchema = z.object({
  id: z.uuid(),
  context_id: z.uuid(),
  service_name: z.string(),
  url: z.string().nullable(),
  username: z.string().nullable(),
  has_password: z.boolean(),
  has_notes: z.boolean(),
  created_by: UserSummarySchema.nullable(),
  updated_by: UserSummarySchema.nullable(),
  last_revealed_at: z.iso.datetime().nullable(),
  last_revealed_by: UserSummarySchema.nullable(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime()
});
var CreateCredentialSchema = z.object({
  service_name: z.string().trim().min(1).max(160),
  url: z.url().max(2048).nullable().optional(),
  username: z.string().trim().max(300).nullable().optional(),
  password: z.string().max(2e3).nullable().optional(),
  notes: z.string().max(1e4).nullable().optional()
});
var UpdateCredentialSchema = CreateCredentialSchema.partial();
var CredentialSecretSchema = z.object({
  id: z.uuid(),
  username: z.string().nullable(),
  password: z.string().nullable(),
  notes: z.string().nullable()
});
var CredentialAccessLogSchema = z.object({
  id: z.uuid(),
  action: z.enum([
    "credentials.reveal",
    "credentials.copy",
    "credentials.create",
    "credentials.update",
    "credentials.delete"
  ]),
  actor: UserSummarySchema.nullable(),
  created_at: z.iso.datetime(),
  ip: z.string().nullable()
});
var ContactPortalSchema = z.object({
  enabled: z.boolean(),
  invited_at: z.iso.datetime().nullable(),
  invite_expires_at: z.iso.datetime().nullable(),
  last_login_at: z.iso.datetime().nullable()
});
var PortalInviteResultSchema = z.object({
  invite_url: z.string(),
  expires_at: z.iso.datetime(),
  /** true quando o e-mail foi enfileirado; false = SMTP indisponível, copie o link. */
  sent_by_email: z.boolean()
});
var PortalLoginSchema = z.object({
  email: z.email().max(254),
  password: z.string().min(1).max(200)
});
var PortalAcceptInviteSchema = z.object({
  token: z.string().min(10).max(200),
  password: z.string().min(8, "A senha precisa ter ao menos 8 caracteres.").max(200)
});
var PortalForgotSchema = z.object({ email: z.email().max(254) });
var PortalSessionSchema = z.object({
  token: z.string(),
  expires_in: z.number().int(),
  contact: z.object({ id: z.uuid(), name: z.string(), email: z.string() }),
  client: z.object({ id: z.uuid(), name: z.string() }),
  organization: z.object({ id: z.uuid(), name: z.string() })
});
var PortalMeSchema = PortalSessionSchema.omit({ token: true, expires_in: true });
var Person = z.object({ name: z.string() });
var PortalTaskSummarySchema = z.object({
  id: z.uuid(),
  number: z.number().int(),
  title: z.string(),
  status: TaskStatus,
  priority: TaskPriority,
  due_at: z.iso.datetime().nullable(),
  is_overdue: z.boolean(),
  completed_at: z.iso.datetime().nullable(),
  responsible: Person.nullable(),
  /** Etapa atual do workflow, quando houver (só o nome). */
  stage: z.string().nullable(),
  subtasks_total: z.number().int(),
  subtasks_completed: z.number().int(),
  /** Aguarda aprovação deste cliente. */
  awaiting_client_approval: z.boolean(),
  updated_at: z.iso.datetime()
});
var PortalCommentSchema = z.object({
  id: z.uuid(),
  author: z.object({ name: z.string(), kind: z.enum(["TEAM", "CLIENT"]) }),
  content_markdown: z.string(),
  attachments: z.array(
    z.object({ id: z.uuid(), filename: z.string(), mime_type: z.string(), size: z.number().int() })
  ),
  created_at: z.iso.datetime()
});
var PortalAttachmentSchema = z.object({
  id: z.uuid(),
  filename: z.string(),
  mime_type: z.string(),
  size: z.number().int(),
  uploaded_by: z.object({ name: z.string(), kind: z.enum(["TEAM", "CLIENT"]) }).nullable(),
  created_at: z.iso.datetime()
});
var PortalHistorySchema = z.object({
  id: z.uuid(),
  action: z.string(),
  actor: z.object({ name: z.string(), kind: z.enum(["TEAM", "CLIENT"]) }).nullable(),
  payload: z.record(z.string(), z.unknown()).nullable(),
  created_at: z.iso.datetime()
});
var PortalTaskDetailSchema = PortalTaskSummarySchema.extend({
  description: z.string().nullable(),
  subtasks: z.array(PortalTaskSummarySchema),
  comments: z.array(PortalCommentSchema),
  attachments: z.array(PortalAttachmentSchema),
  history: z.array(PortalHistorySchema),
  approval_comment: z.string().nullable(),
  can_approve: z.boolean(),
  can_comment: z.boolean()
});
var PortalTaskListQuerySchema = z.object({
  scope: z.enum(["open", "done", "approval", "all"]).default("open"),
  q: z.string().trim().max(120).optional()
});
var PortalCommentInputSchema = z.object({
  content_markdown: z.string().trim().min(1).max(2e4),
  attachment_ids: z.array(z.uuid()).default([])
});
var PortalDecisionSchema = z.object({
  comment: z.string().trim().max(2e3).optional()
});
var PortalPresignSchema = z.object({
  filename: z.string().trim().min(1).max(200).regex(/^[^/\\]+$/, "Nome de arquivo inv\xE1lido."),
  mime_type: z.string().min(1).max(120),
  size: z.number().int().min(1).max(50 * 1024 * 1024)
});
var PortalFormSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  fields: z.array(FormFieldSchema)
});
var PortalSubmitSchema = z.object({
  answers: z.record(z.string(), z.unknown()).default({})
});
var PortalRequestSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  form_name: z.string(),
  status: ExternalRequestStatus,
  created_at: z.iso.datetime(),
  decided_at: z.iso.datetime().nullable(),
  /** Demanda criada, se aprovada e visível ao cliente. */
  task: z.object({ id: z.uuid(), number: z.number().int(), title: z.string() }).nullable()
});
var optText = (max) => z.string().trim().max(max).nullish().transform((v) => v ? v : null);
var CompanyProfileSchema = z.object({
  legal_name: z.string().nullable(),
  trade_name: z.string().nullable(),
  document: z.string().nullable(),
  state_registration: z.string().nullable(),
  municipal_registration: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  website: z.string().nullable(),
  zip_code: z.string().nullable(),
  street: z.string().nullable(),
  number: z.string().nullable(),
  complement: z.string().nullable(),
  district: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable()
});
var CompanyProfileInputSchema = z.object({
  legal_name: optText(200),
  trade_name: optText(200),
  document: optText(32),
  state_registration: optText(32),
  municipal_registration: optText(32),
  email: z.union([z.email().max(254), z.literal("")]).nullish().transform((v) => v ? v : null),
  phone: optText(40),
  website: optText(200),
  zip_code: optText(12),
  street: optText(200),
  number: optText(20),
  complement: optText(100),
  district: optText(100),
  city: optText(100),
  state: optText(2)
});
var LogoVariant = z.enum(["light", "dark", "favicon"]);
var LOGO_MIME_TYPES = ["image/png", "image/jpeg", "image/webp", "image/svg+xml"];
var FAVICON_MIME_TYPES = [
  "image/png",
  "image/svg+xml",
  "image/x-icon",
  "image/vnd.microsoft.icon"
];
var LOGO_MAX_BYTES = 512 * 1024;
var LogoInfoSchema = z.object({
  /** Caminho público (sem autenticação) com cache-busting pela data de atualização. */
  url: z.string(),
  mime_type: z.string(),
  size: z.number().int(),
  updated_at: z.iso.datetime()
});
var BrandingSchema = z.object({
  organization: z.object({ id: z.uuid(), name: z.string() }),
  display_name: z.string(),
  logos: z.object({
    light: LogoInfoSchema.nullable(),
    dark: LogoInfoSchema.nullable(),
    favicon: LogoInfoSchema.nullable()
  })
});
var CompanySettingsSchema = z.object({
  profile: CompanyProfileSchema,
  branding: BrandingSchema,
  /** Agência (clientes, portal, comercial, triagem) ou uso interno (departamentos e projetos). */
  mode: OrganizationMode,
  updated_at: z.iso.datetime().nullable()
});
var UpdateOrganizationModeSchema = z.object({ mode: OrganizationMode });
var LogoUploadSchema = z.object({
  /** Formatos por variante: favicon aceita ICO; logotipos aceitam JPG e WEBP (validado no serviço). */
  mime_type: z.enum([...LOGO_MIME_TYPES, "image/x-icon", "image/vnd.microsoft.icon"]),
  /** Conteúdo em base64 (sem o prefixo data:). */
  data_base64: z.string().min(1).max(Math.ceil(LOGO_MAX_BYTES * 4 / 3) + 8)
});
var MailSource = z.enum(["PANEL", "ENVIRONMENT", "NONE"]);
var MailSettingsSchema = z.object({
  enabled: z.boolean(),
  host: z.string().nullable(),
  port: z.number().int(),
  secure: z.boolean(),
  username: z.string().nullable(),
  /** A senha nunca volta para o front; só se ela está definida. */
  has_password: z.boolean(),
  from_name: z.string().nullable(),
  from_email: z.string().nullable(),
  reply_to: z.string().nullable(),
  /** De onde o envio está saindo agora: painel, variáveis de ambiente do servidor ou nenhum. */
  effective_source: MailSource,
  updated_at: z.iso.datetime().nullable()
});
var MailSettingsInputSchema = z.object({
  enabled: z.boolean(),
  host: optText(200),
  port: z.coerce.number().int().min(1).max(65535).default(587),
  secure: z.boolean().default(false),
  username: optText(200),
  /** undefined = mantém a atual; null ou '' = remove; texto = substitui. */
  password: z.string().max(500).nullish(),
  from_name: optText(120),
  from_email: z.union([z.email().max(254), z.literal("")]).nullish().transform((v) => v ? v : null),
  reply_to: z.union([z.email().max(254), z.literal("")]).nullish().transform((v) => v ? v : null)
}).superRefine((v, ctx) => {
  if (!v.enabled) return;
  if (!v.host)
    ctx.addIssue({ code: "custom", path: ["host"], message: "Informe o servidor SMTP." });
  if (!v.from_email)
    ctx.addIssue({
      code: "custom",
      path: ["from_email"],
      message: "Informe o e-mail remetente."
    });
});
var MailTestSchema = z.object({ to: z.email().max(254) });
var WhatsAppFlavor = z.enum(["go", "api"]);
var WhatsAppSettingsSchema = z.object({
  enabled: z.boolean(),
  flavor: WhatsAppFlavor,
  api_url: z.string().nullable(),
  /** O token/chave nunca volta para o front; só se está definido. */
  has_api_key: z.boolean(),
  instance: z.string().nullable(),
  effective_source: MailSource,
  updated_at: z.iso.datetime().nullable()
});
var WhatsAppSettingsInputSchema = z.object({
  enabled: z.boolean(),
  flavor: WhatsAppFlavor.default("go"),
  api_url: z.union([z.url({ protocol: /^https?$/ }).max(300), z.literal("")]).nullish().transform((v) => v ? v.replace(/\/+$/, "") : null),
  /** undefined = mantém o atual; null ou '' = remove; texto = substitui. */
  api_key: z.string().max(500).nullish(),
  instance: optText(120)
}).superRefine((v, ctx) => {
  if (!v.enabled) return;
  if (!v.api_url)
    ctx.addIssue({ code: "custom", path: ["api_url"], message: "Informe a URL da API." });
  if (v.flavor === "api" && !v.instance)
    ctx.addIssue({ code: "custom", path: ["instance"], message: "Informe o nome da inst\xE2ncia." });
});
var WhatsAppTestSchema = z.object({
  number: z.string().trim().regex(/^\+?[0-9]{10,15}$/, "Use o formato internacional, ex.: +5511999990000.")
});
var TelegramSettingsSchema = z.object({
  enabled: z.boolean(),
  has_bot_token: z.boolean(),
  /** Preenchido automaticamente ao salvar (getMe do Telegram). */
  bot_username: z.string().nullable(),
  effective_source: MailSource,
  updated_at: z.iso.datetime().nullable()
});
var TelegramSettingsInputSchema = z.object({
  enabled: z.boolean(),
  /** undefined = mantém o atual; null ou '' = remove; texto = substitui. */
  bot_token: z.string().trim().max(200).regex(/^(\d+:[\w-]+)?$/, "Token inv\xE1lido: o @BotFather entrega no formato 123456789:ABC\u2026").nullish()
});
var GoogleCalendarSettingsSchema = z.object({
  enabled: z.boolean(),
  client_id: z.string().nullable(),
  /** A chave secreta nunca volta para o front; só se está definida. */
  has_client_secret: z.boolean(),
  /** URI de redirecionamento a cadastrar no Google Cloud (igual para todas as organizações). */
  redirect_uri: z.string(),
  effective_source: MailSource,
  updated_at: z.iso.datetime().nullable()
});
var GoogleCalendarSettingsInputSchema = z.object({
  enabled: z.boolean(),
  client_id: optText(200),
  /** undefined = mantém a atual; null ou '' = remove; texto = substitui. */
  client_secret: z.string().trim().max(200).nullish()
}).superRefine((v, ctx) => {
  if (v.enabled && !v.client_id)
    ctx.addIssue({
      code: "custom",
      path: ["client_id"],
      message: "Informe o ID do cliente OAuth."
    });
  if (v.client_id && !/\.apps\.googleusercontent\.com$/.test(v.client_id))
    ctx.addIssue({
      code: "custom",
      path: ["client_id"],
      message: "O ID do cliente do Google termina em .apps.googleusercontent.com."
    });
});
var MessagingSettingsSchema = z.object({
  whatsapp: WhatsAppSettingsSchema,
  telegram: TelegramSettingsSchema,
  google_calendar: GoogleCalendarSettingsSchema
});
var AccessTokenStatus = z.enum(["ACTIVE", "EXPIRED", "REVOKED"]);
var AccessTokenSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  /** Começo do token, para reconhecer qual é (o token inteiro só aparece na criação). */
  prefix: z.string(),
  status: AccessTokenStatus,
  created_at: z.iso.datetime(),
  expires_at: z.iso.datetime().nullable(),
  last_used_at: z.iso.datetime().nullable(),
  revoked_at: z.iso.datetime().nullable()
});
var ACCESS_TOKEN_TTL_DAYS = [30, 90, 365];
var CreateAccessTokenSchema = z.object({
  name: z.string().trim().min(2, "D\xEA um nome ao token (ex.: Luka).").max(60),
  /** null = sem validade. */
  expires_in_days: z.union([z.literal(30), z.literal(90), z.literal(365)]).nullable().default(90)
});
var CreatedAccessTokenSchema = z.object({
  token: z.string(),
  access_token: AccessTokenSchema,
  /** Endpoint MCP (Streamable HTTP) para configurar no agente. */
  mcp_url: z.string()
});
var NOTE_TITLE_MAX = 200;
var NOTE_CONTENT_MAX = 1e5;
var NOTE_TAG_MAX = 40;
var NOTE_TAGS_MAX = 20;
var fold = (v) => v.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
var NoteTagsSchema = z.array(
  z.string().transform((v) => v.replace(/\s+/g, " ").trim()).pipe(
    z.string().min(1).max(NOTE_TAG_MAX, `Cada tag pode ter at\xE9 ${NOTE_TAG_MAX} caracteres.`)
  )
).transform((tags) => {
  const seen = /* @__PURE__ */ new Set();
  return tags.filter((t) => {
    const k = fold(t);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}).pipe(z.array(z.string()).max(NOTE_TAGS_MAX, `Use at\xE9 ${NOTE_TAGS_MAX} tags por anota\xE7\xE3o.`));
var NoteSortSchema = z.enum(["updated", "created", "title"]);
var NoteListQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  tag: z.string().trim().max(NOTE_TAG_MAX).optional(),
  /** updated (padrão) e created: mais recentes primeiro; title: A–Z. */
  sort: NoteSortSchema.default("updated")
});
var NoteSummarySchema = z.object({
  id: z.uuid(),
  title: z.string(),
  tags: z.array(z.string()),
  /** Começo do texto, sem marcação Markdown. */
  preview: z.string(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime()
});
var NoteSchema = NoteSummarySchema.extend({ content_markdown: z.string() });
var NoteTitle = z.string().trim().min(1, "D\xEA um t\xEDtulo \xE0 anota\xE7\xE3o.").max(NOTE_TITLE_MAX);
var NoteContent = z.string().max(NOTE_CONTENT_MAX);
var CreateNoteSchema = z.object({
  title: NoteTitle,
  content_markdown: NoteContent.default(""),
  tags: NoteTagsSchema.default([])
});
var UpdateNoteSchema = z.object({
  title: NoteTitle.optional(),
  content_markdown: NoteContent.optional(),
  tags: NoteTagsSchema.optional()
}).refine((v) => Object.values(v).some((x) => x !== void 0), { message: "Nada para alterar." });
var NoteTagCountSchema = z.object({ tag: z.string(), count: z.number().int() });
var KanbanPeriod = z.enum(["today", "week", "two_weeks", "month", "all"]);
var KanbanResponsible = z.enum(["all", "me"]);
var MyWorkMode = z.enum(["full", "compact"]);
var BoardFilters = z.object({
  period: KanbanPeriod.catch("week").default("week"),
  responsible: KanbanResponsible.catch("all").default("all"),
  include_no_due: z.boolean().catch(false).default(false)
});
var DEFAULT_BOARD_FILTERS = BoardFilters.parse({});
var BoardKey = z.string().min(1).max(64);
var MAX_BOARDS = 50;
var PipelinesPrefs = z.object({
  /** Último quadro aberto. */
  selected: BoardKey.catch("general").default("general"),
  filters: z.record(BoardKey, BoardFilters.catch(DEFAULT_BOARD_FILTERS)).catch({}).default({})
});
var MyWorkPrefs = z.object({ mode: MyWorkMode.catch("full").default("full") });
var CalendarShow = z.enum(["all", "appointments", "deliveries"]);
var CalendarView = z.enum(["month", "week"]);
var CalendarPrefs = z.object({
  show: CalendarShow.catch("all").default("all"),
  view: CalendarView.catch("month").default("month"),
  mine: z.boolean().catch(false).default(false)
});
function fromLegacy(raw) {
  if (!raw || typeof raw !== "object") return raw;
  const r = raw;
  if (r.pipelines !== void 0 || !r.kanban || typeof r.kanban !== "object") return raw;
  const { kanban, ...rest } = r;
  return { ...rest, pipelines: { selected: "general", filters: { general: kanban } } };
}
var ViewPreferencesSchema = z.preprocess(
  fromLegacy,
  z.object({
    pipelines: PipelinesPrefs.catch(PipelinesPrefs.parse({})).prefault({}),
    my_work: MyWorkPrefs.catch(MyWorkPrefs.parse({})).prefault({}),
    calendar: CalendarPrefs.catch(CalendarPrefs.parse({})).prefault({})
  })
);
var DEFAULT_VIEW_PREFERENCES = ViewPreferencesSchema.parse({});
function boardFilters(prefs, board) {
  return prefs.pipelines.filters[board] ?? DEFAULT_BOARD_FILTERS;
}
function mergeViewPreferences(current, patch) {
  const filters = { ...current.pipelines.filters };
  for (const [board, f] of Object.entries(patch.pipelines?.filters ?? {}))
    filters[board] = { ...filters[board] ?? DEFAULT_BOARD_FILTERS, ...f };
  const keys = Object.keys(filters);
  for (const k of keys.slice(0, Math.max(0, keys.length - MAX_BOARDS))) delete filters[k];
  return ViewPreferencesSchema.parse({
    pipelines: {
      selected: patch.pipelines?.selected ?? current.pipelines.selected,
      filters
    },
    my_work: { ...current.my_work, ...patch.my_work },
    calendar: { ...current.calendar, ...patch.calendar }
  });
}
var ViewPreferencesPatchSchema = z.object({
  pipelines: z.object({
    selected: BoardKey.optional(),
    filters: z.record(
      BoardKey,
      z.object({
        period: KanbanPeriod.optional(),
        responsible: KanbanResponsible.optional(),
        include_no_due: z.boolean().optional()
      }).strict()
    ).optional()
  }).strict().optional(),
  my_work: z.object({ mode: MyWorkMode.optional() }).strict().optional(),
  calendar: z.object({
    show: CalendarShow.optional(),
    view: CalendarView.optional(),
    mine: z.boolean().optional()
  }).strict().optional()
}).strict();

// src/masks.ts
var digitsOf = (v) => v.replace(/\D/g, "");
function normalizeDocument(v) {
  return v.replace(/[^0-9A-Za-z]/g, "").toUpperCase();
}
function applyPattern(chars, pattern) {
  let out = "";
  let i = 0;
  for (const p of pattern) {
    if (i >= chars.length) break;
    if (p === "#") out += chars[i++];
    else out += p;
  }
  return out;
}
function formatCpf(v) {
  return applyPattern(digitsOf(v).slice(0, 11), "###.###.###-##");
}
function formatCnpj(v) {
  const raw = normalizeDocument(v);
  const base = raw.slice(0, 12);
  const dv = digitsOf(raw.slice(12)).slice(0, 2);
  return applyPattern(base + dv, "##.###.###/####-##");
}
function formatDocument(v, personType) {
  return personType === "INDIVIDUAL" ? formatCpf(v) : formatCnpj(v);
}
function formatCpfOrCnpj(v) {
  const raw = normalizeDocument(v);
  return raw.length <= 11 && /^\d*$/.test(raw) ? formatCpf(raw) : formatCnpj(raw);
}
function formatPhone(v) {
  const trimmed = v.trimStart();
  let d = digitsOf(trimmed);
  if (trimmed.startsWith("+") && !d.startsWith("55")) return d ? `+${d.slice(0, 15)}` : "+";
  const intl = trimmed.startsWith("+") || d.length > 11 && d.startsWith("55");
  if (intl) d = d.slice(2);
  d = d.slice(0, 11);
  let local;
  if (d.startsWith("0800") || d.startsWith("0300")) local = applyPattern(d, "#### ### ####");
  else if (d.length <= 2) local = d.length ? `(${d}` : "";
  else if (d.length <= 10) local = applyPattern(d, "(##) ####-####");
  else local = applyPattern(d, "(##) #####-####");
  return intl ? `+55 ${local}`.trimEnd() : local;
}
function displayPhone(v) {
  if (!v) return "";
  const d = digitsOf(v);
  return d.length >= 10 ? formatPhone(v) : v;
}
function displayDocument(v, personType) {
  if (!v) return "";
  return personType ? formatDocument(v, personType) : formatCpfOrCnpj(v);
}
function toWhatsAppNumber(v) {
  if (!v) return null;
  const international = v.trim().startsWith("+");
  const d = digitsOf(v);
  if (international) return d.length >= 10 && d.length <= 15 ? d : null;
  if (d.length === 10 || d.length === 11) return `55${d}`;
  if ((d.length === 12 || d.length === 13) && d.startsWith("55")) return d;
  return null;
}

// src/markdown-tasks.ts
var ITEM = /^(?:[-*+]|\d{1,9}[.)])[ \t]+\[([ xX])\]/;
function toggleTaskAtOffset(source, offset) {
  if (!Number.isInteger(offset) || offset < 0) return source;
  const m = ITEM.exec(source.slice(offset));
  if (!m) return source;
  const at = offset + m[0].length - 2;
  return source.slice(0, at) + (m[1] === " " ? "x" : " ") + source.slice(at + 1);
}

export { ACCESS_TOKEN_TTL_DAYS, ACTIVITY_ACTIONS, ALLOWED_ATTACHMENT_MIME, AVATAR_MAX_BYTES, AVATAR_MIME_TYPES, AccentKey, AccessTokenSchema, AccessTokenStatus, ActivateContractSchema, ActivityEntrySchema, AddDependencySchema, AddParticipantSchema, AddressSchema, ApiErrorSchema, AppearancePatchSchema, AppearanceSchema, ApproveExternalRequestSchema, ApproveProposalSchema, ApproveTaskSchema, AssignTaskSchema, AttachmentEntityType, AttachmentSchema, AuthOrganizationSchema, AuthSessionSchema, AuthUserSchema, AutomationActionSchema, AutomationConditionsSchema, AutomationRuleSchema, AutomationTrigger, AvatarUploadSchema, BrandingSchema, CalendarEventSchema, CalendarIntegrationSchema, CalendarIntegrationStatus, CalendarIntegrationsStateSchema, CalendarProvider, CalendarQuerySchema, CalendarRangeSchema, CalendarShow, CalendarView, CancelTaskSchema, ChangePasswordSchema, ChannelFlagsSchema, ChannelStatusSchema, ChecklistItemSchema, ChecklistSchema, ChecklistTemplateSchema, ClientBaseSchema, ClientContactSchema, ClientListQuerySchema, ClientSchema, CommentSchema, CompanyProfileInputSchema, CompanyProfileSchema, CompanySettingsSchema, CompleteTaskSchema, ConnectCalDavSchema, ContactPortalSchema, ContextFunctionAssignmentSchema, ContextListQuerySchema, ContextNoteSchema, ContextSchema, ContextStatus, ContextType, ContractListQuerySchema, ContractSchema, ContractServiceInputSchema, ContractServiceSchema, ContractStatus, CreateAccessTokenSchema, CreateAutomationRuleSchema, CreateCalendarEventSchema, CreateChecklistItemSchema, CreateChecklistSchema, CreateClientContactSchema, CreateClientSchema, CreateCommentSchema, CreateContextSchema, CreateContractSchema, CreateCredentialSchema, CreateCustomFieldSchema, CreateFormSchema, CreateFunctionSchema, CreateHolidaySchema, CreateNoteSchema, CreatePipelineSchema, CreateProposalSchema, CreateSavedViewSchema, CreateServiceSchema, CreateTagSchema, CreateTaskBlockSchema, CreateTaskSchema, CreateTeamSchema, CreateUserSchema, CreateWorkflowTemplateSchema, CreatedAccessTokenSchema, CredentialAccessLogSchema, CredentialSchema, CredentialSecretSchema, CustomFieldDefinitionSchema, CustomFieldEntity, CustomFieldType, CustomFieldValueSchema, DEFAULT_APPEARANCE, DEFAULT_BOARD_FILTERS, DEFAULT_FONT_MONO, DEFAULT_FONT_SANS, DEFAULT_PIPELINE_STAGES, DEFAULT_PREFERENCES, DEFAULT_VIEW_PREFERENCES, DashboardSchema, DeactivateUserSchema, DeactivationPreviewSchema, DeliveryChannel, DeliveryListQuerySchema, DeliveryStatus, Density, ERROR_CATALOG, ErrorCode, ExternalRequestCountsSchema, ExternalRequestListQuerySchema, ExternalRequestSchema, ExternalRequestStatus, FAVICON_MIME_TYPES, FeedItemSchema, FontMonoKey, FontSansKey, ForceDeleteSchema, FormAccessMode, FormClientRefSchema, FormFieldInputSchema, FormFieldSchema, FormFieldType, FormSchema, FormStatus, FormTokenSchema, FunctionSchema, GoogleCalendarSettingsInputSchema, GoogleCalendarSettingsSchema, HolidaySchema, IdSchema, IntegrationDeliverySchema, IntegrationsHealthSchema, KanbanPeriod, KanbanResponsible, LOGO_MAX_BYTES, LOGO_MIME_TYPES, LoginSchema, LogoInfoSchema, LogoUploadSchema, LogoVariant, MAX_ATTACHMENT_BYTES, MailSettingsInputSchema, MailSettingsSchema, MailSource, MailTestSchema, ManualTimeEntrySchema, MeSchema, MembershipStatus, MessagingSettingsSchema, MovePipelineStageSchema, MoveStageSchema, MyProfileSchema, MyWorkMode, MyWorkSchema, NOTE_CONTENT_MAX, NOTE_TAGS_MAX, NOTE_TAG_MAX, NOTE_TITLE_MAX, NoteListQuerySchema, NoteSchema, NoteSortSchema, NoteSummarySchema, NoteTagCountSchema, NoteTagsSchema, NotificationChannel, NotificationListQuerySchema, NotificationPreferenceSchema, NotificationSchema, NotificationType, OAuthStartSchema, OrgUserSchema, OrganizationMode, OrganizationStatus, PERMISSIONS, PERMISSION_CODES, PIPELINE_CATEGORY_LABEL, PIPELINE_COLORS, PIPELINE_GENERAL, PREFERENCE_EVENTS, PREFERENCE_EVENT_LABEL, PaginationMetaSchema, PaginationQuerySchema, PersonType, PipelineSchema, PipelineSource, PipelineStageCategory, PipelineStageSchema, PortalAcceptInviteSchema, PortalAttachmentSchema, PortalCommentInputSchema, PortalCommentSchema, PortalDecisionSchema, PortalForgotSchema, PortalFormSchema, PortalHistorySchema, PortalInviteResultSchema, PortalLoginSchema, PortalMeSchema, PortalPresignSchema, PortalRequestSchema, PortalSessionSchema, PortalSubmitSchema, PortalTaskDetailSchema, PortalTaskListQuerySchema, PortalTaskSummarySchema, PresignAttachmentSchema, PresignedUploadSchema, ProposalItemInputSchema, ProposalItemSchema, ProposalListQuerySchema, ProposalSchema, ProposalStatus, PublicFormSchema, PublishWorkflowSchema, RecurrenceRuleSchema, RejectExternalRequestSchema, RejectProposalSchema, RejectTaskSchema, ReportFormat, ReportPeriodSchema, ReportQuerySchema, ReportRowSchema, ReportSchema, RequestAnswerSchema, RequestApprovalSchema, RequesterInputSchema, RoleSchema, RoleSummarySchema, SYSTEM_ROLE_PRESETS, SavedViewSchema, ServiceSchema, ServiceStatus, SetContextFunctionSchema, SetContextNoteSchema, SetCustomFieldValuesSchema, SetFormFieldsSchema, SetNotificationPreferencesSchema, SetRecurrenceSchema, SetTeamMembersSchema, SetUserRolesSchema, SetWhatsAppNumberSchema, SlaUnit, SlugSchema, StageInstanceSchema, StageTemplateInputSchema, StageTemplateSchema, StartTimerSchema, StartWorkflowSchema, StopTimerSchema, SubmitFormAuthenticatedSchema, SubmitFormSchema, SubmitResultSchema, SystemRole, TagSchema, TagType, TaskAbilitiesSchema, TaskApprovalSchema, TaskBlockSchema, TaskContextRefSchema, TaskDependencySchema, TaskDetailSchema, TaskListQuerySchema, TaskPipelineRefSchema, TaskPriority, TaskRefSchema, TaskScope, TaskSortField, TaskSource, TaskStatus, TaskSummarySchema, TaskSummaryWithWorkflowSchema, TaskTemplateInputSchema, TaskTemplateSchema, TaskTimeSchema, TaskWorkflowRefSchema, TeamMemberInputSchema, TeamMemberRole, TeamMemberSchema, TeamSchema, TelegramLinkCodeSchema, TelegramSettingsInputSchema, TelegramSettingsSchema, TemplateDependencyInputSchema, ThemeMode, TimeEntrySchema, TimeEntrySource, UpdateAutomationRuleSchema, UpdateCalendarEventSchema, UpdateChecklistItemSchema, UpdateChecklistSchema, UpdateClientContactSchema, UpdateClientSchema, UpdateCommentSchema, UpdateContextSchema, UpdateContractSchema, UpdateCredentialSchema, UpdateCustomFieldSchema, UpdateFormSchema, UpdateFunctionSchema, UpdateMyProfileSchema, UpdateNoteSchema, UpdateOrganizationModeSchema, UpdatePipelineSchema, UpdateProposalSchema, UpdateSavedViewSchema, UpdateServiceSchema, UpdateTagSchema, UpdateTaskBlockSchema, UpdateTaskSchema, UpdateTeamSchema, UpdateUserSchema, UpdateWorkflowTemplateSchema, UserListQuerySchema, UserStatus, UserSummarySchema, ViewPreferencesPatchSchema, ViewPreferencesSchema, WeeklyQuerySchema, WeeklySchema, WhatsAppFlavor, WhatsAppSettingsInputSchema, WhatsAppSettingsSchema, WhatsAppTestSchema, WorkflowInstanceSchema, WorkflowTemplateSchema, WorkflowVersionSchema, WorkflowVersionSummarySchema, boardFilters, categoryOfStatus, displayDocument, displayPhone, formatCnpj, formatCpf, formatCpfOrCnpj, formatDocument, formatPhone, mergeViewPreferences, normalizeDocument, toWhatsAppNumber, toggleTaskAtOffset };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map